import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { SearchDetailFilter } from '../../interfaces/search-detail-filter';
import {
  switchMap,
  takeUntil,
  Subject,
  debounceTime,
  from,
  retry,
  throwError,
  of,
  forkJoin,
  tap,
} from 'rxjs';
import {
  Assignee,
  AssigneeConnectionDetails,
  Company,
} from '../../interfaces/company';
import {
  ConnectionUser,
  SharedConnectionUsers,
} from '../../models/shared-connection-users';
import { AssignLeadResponse } from '../../../pages/leads/models/assign-lead-body';
import { CompanyDetails, Properties } from '../../models/company-details';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  Validators,
  ValidatorFn,
} from '@angular/forms';
import { PdfDownloadService } from '../../services/pdf-download/pdf-download.service';
import { LeadsService } from '../../../pages/leads/services/leads.service';
import { SearchService } from '../../../pages/search/services/search.service';
import { LazyWidgetLoaderService } from '../../../plugins/services/lazy-widget-loader.service';
import { DestroyBase } from '../../base-classes/destroy-component-base';
import { DatePipe } from '@angular/common';
import { PopupComponent } from '../popup/popup.component';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { SecondaryPopupComponent } from '../secondary-popup/secondary-popup.component';
import { AuthService } from '../../../pages/auth/services/auth.service';
import { ClickOutsideDirective } from '../../directives/click-outside/click-outside.directive';
import { NotificationsService } from '../../../pages/notifications/services/notifications.service';
import { UserSuggestionResponse } from '../../../pages/search/models/user-suggestion';
import { Router } from '@angular/router';
import { ShareRecord } from '../../../pages/search/models/share-record';
import { OutreachService } from '../../../pages/outreach/services/outreach.service';
import { OutreachFormTypes } from '../../../pages/outreach/models/outreach-form-data';
import { INDUSTRY_OPTIONS } from '../../../pages/outreach/models/leads-criteria.model';
import { CommonModule } from '@angular/common';
import { ArcgisService } from '../../../pages/dashboard/services/arcgis/arcgis.service';
import { LoggedInUser } from '../../models/logged-in-user';
import { RecordLeadsHistory } from '../../../pages/search/models/record-leads-history';
import { RecordNotesComponent } from '../record-notes/record-notes.component';
import { RecordScoresComponent } from '../record-scores/record-scores.component';
import { RecordAssigneesComponent } from '../record-assignees/record-assignees.component';
import { RecordQuotaComponent } from '../record-quota/record-quota.component';
import { RecordCacheComponent } from '../record-cache/record-cache.component';
import { RecordMatchedCDFIsComponent } from '../record-matched-cdfis/record-matched-cdfis.component';
import { CRMService } from '../../services/CRM/crm.service';
import { SettingsService } from '../../services/settings/settings.service';
import { environment } from '../../../../environments/environment';
import { PermissionCode } from '../../enums/permission-code';
import { RecordVisibilityPopupComponent } from '../record-visibility-popup/record-visibility-popup.component';
import { RecordVisibilityMode } from '../../enums/record-visibility';
import { InvitePopupComponent } from '../invite-popup/invite-popup.component';
import { AuditLogService } from '../../services/audit-log/audit-log.service';
import { RecordAttachmentsComponent } from '../record-attachments/record-attachments.component';
import { AddressService } from '../../services/address/address.service';
import { State, County, City, Zipcode } from '../../models/address';
import { RecordArcgisComponent } from '../record-arcgis/record-arcgis.component';
import { RecordLocationIntelligenceComponent } from '../record-location-intelligence/record-location-intelligence.component';
import { RecordLeadInfoComponent } from '../record-lead-info/record-lead-info.component';
import { LeadPipelineTrackerComponent } from '../lead-pipeline-tracker/lead-pipeline-tracker.component';
import { UnassignLeadPopupComponent } from '../unassign-lead-popup/unassign-lead-popup.component';
import { ToastType } from '../../interfaces/common';
import { LeadStatusValues } from '../../../pages/leads/models/lead-status';
import { CommonService } from '../../services/common/common.service';
import { DefaultLeadAttributes } from '../../models/lead-info';
import {
  NumberValidatorService,
  NumberConstraints,
} from '../../services/number-validator/number-validator.service';
import { EntityType, ReportContentData } from '../../interfaces/report-content';
import { ReportContentService } from '../../services/reports/report-content.service';

interface ExtendedConnectionUser extends ConnectionUser {
  name: string;
}

interface SelectedUser {
  username: string;
  displayName: string;
}

interface GroupedProperties {
  group: string | null;
  properties: Properties[];
}

@Component({
  selector: 'app-record-details',
  standalone: true,
  imports: [
    RecordLocationIntelligenceComponent,
    CommonModule,
    DatePipe,
    PopupComponent,
    SvgIconComponent,
    ReactiveFormsModule,
    SecondaryPopupComponent,
    ClickOutsideDirective,
    FormsModule,
    RecordNotesComponent,
    RecordScoresComponent,
    RecordAssigneesComponent,
    RecordQuotaComponent,
    RecordCacheComponent,
    RecordMatchedCDFIsComponent,
    RecordVisibilityPopupComponent,
    InvitePopupComponent,
    RecordAttachmentsComponent,
    RecordArcgisComponent,
    RecordLeadInfoComponent,
    LeadPipelineTrackerComponent,
    UnassignLeadPopupComponent,
  ],
  templateUrl: './record-details.component.html',
  styleUrl: './record-details.component.scss',
})
export class RecordDetailsComponent extends DestroyBase implements OnChanges {
  @Input() company!: Company;
  @Input() unique_id: string | null = null;
  @Input() showNotes = false;
  @Input() isActionButtonsAvailable = true;
  @Output() popupClosed = new EventEmitter();
  @Output() assignedLead = new EventEmitter<Company | null>();
  @Output() companyDetailsUpdated = new EventEmitter<Company>();
  @Output() companyDeleted = new EventEmitter<string>();
  @Output() leadStatusUpdated = new EventEmitter<{
    uniqueId: string;
    newStatus: string;
  }>();
  @Output() triggerSearchResults = new EventEmitter<boolean>();

  @ViewChild('pdfcontent')
  private pdfContent!: ElementRef;

  @ViewChild('leadAttributesToggleContainer', { read: ViewContainerRef })
  leadAttributesToggleContainer!: ViewContainerRef;

  @ViewChild(RecordLeadInfoComponent)
  leadInfoComponent!: RecordLeadInfoComponent;

  private readonly areaCode = 'search-detail';
  lazyWidgetLoaderService = inject(LazyWidgetLoaderService);

  searchService = inject(SearchService);
  authService = inject(AuthService);
  leadsService = inject(LeadsService);
  private reportContentService = inject(ReportContentService);

  openReportModal(): void {
    const payload: ReportContentData = {
      entityId: this.unique_id || this.recordDetails?.unique_id || '',
      entityType: EntityType.BUSINESS,
      title: 'Flag Business Record',
      itemTitle: this.company.company_name || this.title || 'Business Record',
    };
    this.reportContentService.open(payload);
  }

  pdfDownloadService = inject(PdfDownloadService);
  notificationService = inject(NotificationsService);
  router = inject(Router);
  outreachService = inject(OutreachService);
  arcgisService = inject(ArcgisService);
  crmService = inject(CRMService);
  settingsService = inject(SettingsService);
  auditLogService = inject(AuditLogService);
  addressService = inject(AddressService);
  private commonService = inject(CommonService);
  private numberValidatorService = inject(NumberValidatorService);

  showUnassignPopup = false;
  private vlfUnassigned = false;
  isUnassigning = false;

  recordDetails: CompanyDetails | undefined;
  propertyForm: FormGroup;
  canEditDelete = false;
  isDirty = false;

  applicationSummary: string | null = null;
  originalApplicationSummary: string | null = null;
  applicationSummaryExpanded = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updatedProperties: Record<string, any> = {};
  showUnsavedChangesPopup = false;
  primaryAction = 'Edit';
  isEditClicked = false;
  secondaryAction = '';
  showDeletePopup = false;
  loggedInUser: string | undefined;
  loggedInUserInfo: LoggedInUser | null = null;
  showUsersDropdown = false;
  userSuggestions!: UserSuggestionResponse[];
  selectedPeopleToShare: string[] = [];
  selectedUsersWithNames: SelectedUser[] = [];
  selectedConnections: Record<string, SharedConnectionUsers> = {};

  selectedThroughConnections = new Set<string>();
  private searchSubject = new Subject<string>();
  isLoadingUsers = true;
  isAdminUser = this.authService.hasPermission(PermissionCode.ADMIN_PRIVILEGES);
  hasDataAxlePermission = this.authService.hasPermission(
    PermissionCode.ACCESS_DATA_AXLE,
  );
  hasViewOpportunityIndicatorPermission = this.authService.hasPermission(
    PermissionCode.VIEW_OPPORTUNITY_INDICATOR,
  );
  hideSecondaryButton = false;
  title = '';
  hasMoreUsers = false;
  isLoadingMore = false;
  showSendOutreachEmailConfirm = false;
  selectedOutreachFormType: string | null = null;
  outreachFormTypes = OutreachFormTypes;
  isDownloadingPdf = false;
  leadsHistory: RecordLeadsHistory[] = [];
  showLeadsHistoryInline = false;
  isLoadingLeadsHistory = false;
  crmStatus = false;
  crmLeadId: string | null | undefined = null;

  // Properties for quota animation
  isAssignButtonHovered = false;

  recordErrorMessage = '';
  showScore = false;
  canShowScore = false;
  additionalDetailsExpanded: Record<
    string,
    Record<string, boolean> | undefined
  > = {};
  isLoadingCRM = false;
  crmError: string | null = null;
  showRecordVisibilityControl = false;
  showVisibilityModal = false;
  recordVisibilityModes = RecordVisibilityMode;
  showScoreDataSourcePopup = false;
  useCustomData: boolean | null = null;
  availableConnections: SharedConnectionUsers[] = [];
  availableUsers: ExtendedConnectionUser[] = [];
  isLoadingConnections = false;
  isLoadingConnectionUsers = false;
  hasMoreConnections = false;
  isLoadingMoreConnections = false;
  connectionCurrentPage = 1;
  connectionPageSize = 3;

  selectedConnection: SharedConnectionUsers | null = null;
  userCurrentPage = 1;
  userPageSize = 3;

  userSearchKey = '';
  private userSearchSubject = new Subject<string>();
  showInvitePopup = false;
  showArcgisPopup = false;
  preloadedArcgisData: Record<string, unknown> | undefined = undefined;

  // State-County data
  states: State[] = [];
  counties: County[] = [];
  cities: City[] = [];
  zipcodes: Zipcode[] = [];
  availableStates: string[] = [];
  availableCounties: string[] = [];
  availableCities: string[] = [];
  availableZipcodes: string[] = [];
  selectedStateForCounty = '';

  // Loan type change tracking
  originalLoanType: string | null = null;
  showLoanTypeChangeWarning = false;
  loanRelatedProperties = [
    'project_description',
    'contributing_personal_capital',
    'personal_capital_percentage',
    'collateral_types',
    'reporting_period',
    'period_months',
    'net_profit',
    'depreciation_amortization',
    'total_assets',
    'cash_equivalents',
    'receivables',
    'inventory_value',
    'current_liabilities',
    'shareholder_equity',
    'alternative_net_worth',
    'total_personal_income',
    'personal_net_worth',
    'personal_loans_amount',
    'existing_loans_amount',
    'credit_lines_limit',
    'monthly_debt_service',
    'custom_months',
  ];

  // Interested Products filtering
  allowedGroups: string[] | null = null; // null means show all groups

  // Table editing state
  tableEditData: Record<string, unknown[]> = {};

  private fundedStatuses = ['Active Funded', 'Active Funded Grant'];
  private originalApprovedAmount: string | number | null = null;

  isVlfActiveFunded(): boolean {
    const status = this.recordDetails?.lead_attributes?.['lead_status'];
    return (
      this.recordDetails?.source_type?.toLowerCase() === 'vlf' &&
      status != null &&
      Number(status) === Number(LeadStatusValues['active_funded'])
    );
  }

  get hideAssignToMe(): boolean {
    return (
      !this.authService.hasPermission(PermissionCode.GET_SET_LEADS) ||
      this.isVlfActiveFunded()
    );
  }

  private isFundedLeadStatus(): boolean {
    const statusProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property === 'lead_info::lead_status',
    );

    if (!statusProperty) return false;

    // Get value
    const rawValue = this.isEditClicked
      ? (this.propertyForm.get(statusProperty.property_name)?.value ??
        statusProperty.value)
      : statusProperty.value;

    // Convert to label
    const label =
      this.getOptionLabel(statusProperty.options, rawValue) || rawValue;

    return this.fundedStatuses.includes(label);
  }

  private isActiveFundedGrantLeadStatus(): boolean {
    const statusProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property === 'lead_info::lead_status',
    );

    if (!statusProperty) return false;

    const rawValue = this.isEditClicked
      ? (this.propertyForm.get(statusProperty.property_name)?.value ??
        statusProperty.value)
      : statusProperty.value;

    const label =
      this.getOptionLabel(statusProperty.options, rawValue) || rawValue;

    return label === 'Active Funded Grant';
  }

  getPropertyLabel(property: Properties): string {
    if (
      property.property === 'lead_info::approved_loan_amount' &&
      this.isActiveFundedGrantLeadStatus()
    ) {
      return 'Approved Grant Amount';
    }

    if (property.property?.toLowerCase().includes('business_address')) {
      const hasBusinessProp = this.recordDetails?.detail.properties.find((p) =>
        p.property?.toLowerCase().includes('has_business'),
      );
      if (hasBusinessProp) {
        const rawValue = this.isEditClicked
          ? (this.propertyForm.get(hasBusinessProp.property_name)?.value ??
            hasBusinessProp.value)
          : hasBusinessProp.value;
        if (String(rawValue).toLowerCase() !== 'yes') {
          return 'Address Line 1';
        }
      }
      return 'Business Address Line 1';
    }

    return property.property_name;
  }

  constructor(private fb: FormBuilder) {
    super();
    this.propertyForm = this.fb.group({});

    // Setup debounced search
    this.searchSubject
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe(() => {
        this.getAllConnections();
      });

    // Setup debounced user search
    this.userSearchSubject
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe((searchTerm) => {
        this.userSearchKey = searchTerm;
        if (this.selectedConnection) {
          this.loadUsersForConnection(this.selectedConnection.id);
        }
      });
  }

  loadLeadAttributes(leadId: number) {
    return from(
      this.lazyWidgetLoaderService.loadWidget(
        this.areaCode,
        this.leadAttributesToggleContainer,
        { leadId: leadId },
      ),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    let searchDetailFilter: SearchDetailFilter = {};
    if (changes['company'] && changes['company'].currentValue) {
      searchDetailFilter = {
        unique_id: this.company.unique_id,
        category: '101',
        source: this.company.source,
      };
      this.title = this.company.company_name;
    } else if (changes['unique_id'] && changes['unique_id'].currentValue) {
      searchDetailFilter = {
        unique_id: this.unique_id!,
        category: '101',
      };
    }
    this.loggedInUserInfo = this.authService.getLoggedInUserInfo();
    this.loggedInUser = this.loggedInUserInfo?.username;
    this.loadCompanyDetails(searchDetailFilter);
  }

  loadCompanyDetails(searchDetailFilter: SearchDetailFilter): void {
    this.searchService
      .getCompanyDetails(searchDetailFilter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (!searchDetailFilter.source && !this.company) {
            const companyName =
              res.detail.properties.find(
                (p) => p.property.toLowerCase() === 'company_name',
              )?.value ||
              res.detail.properties.find(
                (p) => p.property.toLowerCase() === 'business_name',
              )?.value ||
              '';
            this.title = companyName;

            this.company = {
              leadId: undefined,
              unique_id: res.unique_id,
              company_name: companyName,
              short_address:
                res.detail.properties.find(
                  (p) => p.property.toLowerCase() === 'short_address',
                )?.value || '',
              latitude: res.latitude,
              longitude: res.longitude,
              assigned_to: res.assigned_to || null,
              source: res.source_type,
              created_by_user: res.created_by_user || null,
              created_on_utc: new Date(res.created_on_utc),
              assignees: Array.isArray(res.assignees) ? res.assignees : [],
              is_active: true,
              lead_attributes: res.lead_attributes ?? {
                ...DefaultLeadAttributes,
              },
            };
          }
          this.recordDetails = res;

          this.recordDetails.lead_attributes = res.lead_attributes ?? {
            ...DefaultLeadAttributes,
          };
          const applicationSummaryProperty = res.detail.properties.find(
            (prop) => prop.property === 'application_summary',
          );
          this.originalApplicationSummary =
            applicationSummaryProperty?.value ?? null;
          this.showRecordVisibilityControl = this.canUpdateRecordVisibility();

          this.checkEditDelete();

          // Initialize allowed groups for view mode filtering
          this.initializeAllowedGroups(res);

          // const hasTotalScore = this.recordDetails.detail.properties.some(
          //   (p) => p.property === 'total_score',
          // );
          const hasTotalScore = false; // Hide WNCSBI score for now

          if (hasTotalScore || this.isLoanFormFilled()) {
            this.canShowScore = true;
          }

          // Update visibility mode in company object if it changed
          if (
            this.recordDetails.visibility_mode !== this.company.visibility_mode
          ) {
            this.company.visibility_mode = this.recordDetails.visibility_mode;
            this.companyDetailsUpdated.emit(this.company);
          }

          if (!this.recordDetails.last_outreach_date) {
            this.outreachFormTypes = this.outreachFormTypes.filter(
              (type) => type.label !== 'Loan Application',
            );
          }
          if (this.recordDetails.assigned_to) {
            this.loadCRMStatus();
          }
        },
        error: (err) => {
          if (err.status === 403) {
            this.recordErrorMessage =
              'You do not have permission to view this record.';
          } else if (err.status === 423) {
            this.recordErrorMessage = err.error['detail'];
          } else {
            this.recordErrorMessage =
              'Failed to load record details! Please try again later.';
          }
        },
      });
  }

  loadCRMStatus(): void {
    if (this.recordDetails?.assigned_to) {
      this.crmStatus =
        localStorage.getItem('crm_status')?.toLowerCase() === 'true' ||
        this.recordDetails?.crm_lead_id !== null;
      this.crmLeadId = this.recordDetails?.crm_lead_id;
    } else {
      // Reset CRM lead ID if user is not assigned
      this.crmLeadId = null;
    }
  }

  handleLoadLeadsHistory(uniqueId: string): void {
    this.auditLogService.logFeature({
      featureName: 'lead',
      action: 'view_history',
      meta: {
        recordId: uniqueId,
        recordName: this.company?.company_name || '',
      },
      description: 'User views lead history',
    });

    this.isLoadingLeadsHistory = true;
    this.searchService
      .getRecordLeadsHistory(uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          // Sort by updated_on_utc date (newest first)
          this.leadsHistory = res.sort((a, b) => {
            const dateA = new Date(a.updated_on_utc);
            const dateB = new Date(b.updated_on_utc);
            return dateB.getTime() - dateA.getTime();
          });
          this.isLoadingLeadsHistory = false;
        },
        error: () => {
          this.isLoadingLeadsHistory = false;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: 'Failed to load leads history!',
          });
          this.leadsHistory = [];
        },
      });
  }

  checkEditDelete(): void {
    if (!this.recordDetails?.has_access) {
      this.canEditDelete = false;
      return;
    }

    const isEditableForm = this.recordDetails!.detail.properties.some(
      (p) => p.isEditable == true,
    );

    const isCreator =
      this.recordDetails?.created_by_user?.username === this.loggedInUser;

    // Check if user can edit
    const canEdit =
      ((this.recordDetails ? this.recordDetails.assigned_to : false) ||
        isCreator) &&
      (isEditableForm || this.recordDetails?.lead_attributes != null);

    // Check if user can delete
    const canDelete =
      this.isAdminUser ||
      isCreator ||
      ((this.recordDetails ? this.recordDetails.assigned_to : false) &&
        this.recordDetails!.source_type === 'CUSTOM' &&
        this.authService.hasPermission(PermissionCode.CUSTOM_BUSINESS_RECORD));

    if (canEdit && canDelete) {
      this.canEditDelete = true;
      this.primaryAction = 'Edit';
      this.secondaryAction = 'Delete';
      this.hideSecondaryButton = false;
    } else if (canEdit && !canDelete) {
      this.canEditDelete = true;
      this.primaryAction = 'Edit';
      this.secondaryAction = '';
      this.hideSecondaryButton = true;
    } else if (!canEdit && canDelete) {
      this.canEditDelete = true;
      this.primaryAction = 'Delete';
      this.secondaryAction = '';
      this.hideSecondaryButton = true;
    } else {
      this.canEditDelete = false;
      this.primaryAction = '';
      this.secondaryAction = '';
      this.hideSecondaryButton = false;
    }
  }

  canUpdateRecordVisibility(): boolean {
    if (this.company?.source === 'SME') {
      return false;
    }

    return (
      this.recordDetails?.created_by_user?.connection?.connection_name ===
        this.loggedInUserInfo?.connection &&
      ((this.recordDetails?.source_type === 'CUSTOM' &&
        this.authService.hasPermission(
          PermissionCode.CUSTOM_BUSINESS_RECORD,
        )) ||
        (this.recordDetails?.source_type === 'PUBLIC' &&
          this.authService.hasPermission(PermissionCode.ACCESS_DEEPROOTS)))
    );
  }

  initializeForm(details: CompanyDetails) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const group: Record<string, any> = {};

    details.detail.properties.forEach((prop) => {
      if (prop.isEditable) {
        let defaultValue: unknown = prop.value;

        if (prop.type === 'dropdown' && !prop.value) {
          defaultValue = '';
        } else if (prop.type === 'dropdown' || prop.type === 'radio') {
          if (
            prop.value !== null &&
            prop.value !== undefined &&
            prop.value !== '' &&
            prop.options
          ) {
            // Check if current value is a label and convert to option value
            const optionValue = this.getOptionValue(prop.options, prop.value);
            // If we found a matching option value, use it; otherwise keep the original (might already be a value)
            defaultValue = optionValue !== null ? optionValue : prop.value;
          } else {
            defaultValue = prop.value || '';
          }
        } else if (prop.type === 'multiselect') {
          // For multiselect, convert labels to option values
          if (Array.isArray(prop.value)) {
            // Convert array of labels to array of option values
            defaultValue = prop.value.map((label) => {
              const optionValue = this.getOptionValue(
                prop.options,
                String(label),
              );
              // If we found a matching option value, use it; otherwise keep the original (might already be a value)
              return optionValue !== null ? optionValue : label;
            });
          } else {
            defaultValue = [];
          }
        }
        // Add dynamic validators based on schema constraints
        const validators = this.getPropertyValidators(prop);
        group[prop.property_name] = [defaultValue, validators];
      }
    });

    this.propertyForm = this.fb.group(group);

    // Store original loan type
    const loanTypeProperty = details.detail.properties.find(
      (p) => p.property.toLowerCase() === 'loan_type',
    );
    if (loanTypeProperty) {
      this.originalLoanType = String(loanTypeProperty.value || '');
    }

    // Initialize allowed groups based on Interested Products
    this.initializeAllowedGroups(details);
  }

  onPropertyUpdate(propertyName: string, event: Event) {
    const target = event.target as HTMLInputElement;

    const property = this.recordDetails?.detail.properties.find(
      (p) => p.property_name === propertyName,
    );

    // If status changed to Funded, restore the value immediately to prevent error flash
    if (property?.property === 'lead_info::lead_status') {
      const statusLabel =
        this.getOptionLabel(property.options, target.value) || target.value;
      const isFunded = this.fundedStatuses.includes(String(statusLabel));

      if (isFunded && this.originalApprovedAmount !== null) {
        const approvedProperty = this.recordDetails?.detail.properties.find(
          (p) => p.property === 'lead_info::approved_loan_amount',
        );
        if (approvedProperty) {
          const approvedControl = this.propertyForm.get(
            approvedProperty.property_name,
          );
          if (approvedControl) {
            // Restore immediately before the timeout
            approvedControl.setValue(this.originalApprovedAmount, {
              emitEvent: false,
            });
            this.updatedProperties[approvedProperty.property_name] =
              this.originalApprovedAmount;
          }
        }
      }
    }

    // For radio buttons and dropdowns, get value from form control after Angular updates it
    // Use setTimeout to ensure form control is updated
    setTimeout(() => {
      const formValue = this.propertyForm.get(propertyName)?.value;
      const value = formValue !== undefined ? formValue : target.value;

      // Check if this is a loan type change
      const property = this.recordDetails?.detail.properties.find(
        (p) => p.property_name === propertyName,
      );

      // Check if this is contributing_personal_capital change to No (false value)
      if (
        property?.property.toLowerCase() === 'contributing_personal_capital' &&
        value === false
      ) {
        // Clear personal_capital_percentage
        const percentageProperty = this.recordDetails?.detail.properties.find(
          (p) => p.property.toLowerCase() === 'personal_capital_percentage',
        );
        if (percentageProperty) {
          this.propertyForm.patchValue(
            {
              [percentageProperty.property_name]: '',
            },
            { emitEvent: false },
          );
          // Set to empty string, will be converted to null in payload
          this.updatedProperties[percentageProperty.property_name] = '';
        }
      }

      // Check if this is period_months change to non-Other value
      if (
        property?.property.toLowerCase() === 'period_months' &&
        value !== 'Other'
      ) {
        // Clear custom_months
        const customMonthsProperty = this.recordDetails?.detail.properties.find(
          (p) => p.property.toLowerCase() === 'custom_months',
        );
        if (customMonthsProperty) {
          this.propertyForm.patchValue(
            {
              [customMonthsProperty.property_name]: '',
            },
            { emitEvent: false },
          );
          // Set to empty string, will be converted to null in payload
          this.updatedProperties[customMonthsProperty.property_name] = '';
        }
      }

      // Check if this is knows_investor_type change to non-yes value
      if (
        property?.property.toLowerCase() === 'knows_investor_type' &&
        value !== 'yes'
      ) {
        // Clear investment_types
        const investmentTypesProperty =
          this.recordDetails?.detail.properties.find(
            (p) => p.property.toLowerCase() === 'investment_types',
          );
        if (investmentTypesProperty) {
          this.propertyForm.patchValue(
            {
              [investmentTypesProperty.property_name]: [],
            },
            { emitEvent: false },
          );
          // Set to empty array for multiselect
          this.updatedProperties[investmentTypesProperty.property_name] = [];
        }
      }

      // Check if this is looking_to_upgrade_facility change to non-yes/true value
      if (
        property?.property.toLowerCase() === 'looking_to_upgrade_facility' &&
        value !== true &&
        value !== 'Yes'
      ) {
        // Clear own_facility
        const ownFacilityProperty = this.recordDetails?.detail.properties.find(
          (p) => p.property.toLowerCase() === 'own_facility',
        );
        if (ownFacilityProperty) {
          this.propertyForm.patchValue(
            {
              [ownFacilityProperty.property_name]: '',
            },
            { emitEvent: false },
          );
          this.updatedProperties[ownFacilityProperty.property_name] = '';
        }

        // Also clear building_owner_willing_to_support (cascading clear)
        const buildingOwnerProperty =
          this.recordDetails?.detail.properties.find(
            (p) =>
              p.property.toLowerCase() === 'building_owner_willing_to_support',
          );
        if (buildingOwnerProperty) {
          this.propertyForm.patchValue(
            {
              [buildingOwnerProperty.property_name]: '',
            },
            { emitEvent: false },
          );
          this.updatedProperties[buildingOwnerProperty.property_name] = '';
        }
      }

      // Check if this is own_facility change to non-No/false value
      if (
        property?.property.toLowerCase() === 'own_facility' &&
        value !== false &&
        value !== 'No'
      ) {
        // Clear building_owner_willing_to_support
        const buildingOwnerProperty =
          this.recordDetails?.detail.properties.find(
            (p) =>
              p.property.toLowerCase() === 'building_owner_willing_to_support',
          );
        if (buildingOwnerProperty) {
          this.propertyForm.patchValue(
            {
              [buildingOwnerProperty.property_name]: '',
            },
            { emitEvent: false },
          );
          this.updatedProperties[buildingOwnerProperty.property_name] = '';
        }
      }

      // Check if this is business_help_aspects change and 'Business Succession' is not included
      if (property?.property.toLowerCase() === 'business_help_aspects') {
        const businessHelpAspects = Array.isArray(value) ? value : [];
        const hasBusinessSuccession = businessHelpAspects.some(
          (aspect: string | number | boolean) =>
            String(aspect).toLowerCase() === 'business succession' ||
            String(aspect).toLowerCase() === 'business_succession',
        );

        if (!hasBusinessSuccession) {
          // Clear succession-related properties
          const successionProperties = [
            'interested_in_selling_business',
            'interested_in_employee_strategies',
            'interested_in_succession_planning',
          ];

          successionProperties.forEach((propKey) => {
            const prop = this.recordDetails?.detail.properties.find(
              (p) => p.property.toLowerCase() === propKey,
            );
            if (prop) {
              this.propertyForm.patchValue(
                {
                  [prop.property_name]: '',
                },
                { emitEvent: false },
              );
              this.updatedProperties[prop.property_name] = '';
            }
          });
        }
      }

      // Check if this is will_fill_prescreening change to non-No/false value
      if (
        property?.property.toLowerCase() === 'will_fill_prescreening' &&
        value !== false &&
        value !== 'No'
      ) {
        // Clear prescreening-related properties
        const prescreeningProperties = [
          'credit_score_range',
          'has_co_signer',
          'has_business_plan',
          'has_financial_projections',
        ];

        prescreeningProperties.forEach((propKey) => {
          const prop = this.recordDetails?.detail.properties.find(
            (p) => p.property.toLowerCase() === propKey,
          );
          if (prop) {
            this.propertyForm.patchValue(
              {
                [prop.property_name]: '',
              },
              { emitEvent: false },
            );
            this.updatedProperties[prop.property_name] = '';
          }
        });
      }

      // Check if this is will_fill_prescreening change to No/false value
      if (
        property?.property.toLowerCase() === 'will_fill_prescreening' &&
        (value === false || value === 'No')
      ) {
        // Clear detailed loan properties
        const detailedLoanProperties = [
          'project_description',
          'contributing_personal_capital',
          'personal_capital_percentage',
          'collateral_types',
          'reporting_period',
          'period_months',
          'net_profit',
          'depreciation_amortization',
          'total_assets',
          'cash_equivalents',
          'receivables',
          'inventory_value',
          'current_liabilities',
          'shareholder_equity',
          'alternative_net_worth',
          'total_personal_income',
          'personal_net_worth',
          'personal_loans_amount',
          'existing_loans_amount',
          'credit_lines_limit',
          'monthly_debt_service',
          'ownership_structure',
        ];

        detailedLoanProperties.forEach((propKey) => {
          const prop = this.recordDetails?.detail.properties.find(
            (p) => p.property.toLowerCase() === propKey,
          );
          if (prop) {
            // Handle different field types appropriately
            if (
              prop.type === 'multiselect' ||
              prop.type === 'list' ||
              prop.type === 'table' ||
              propKey === 'ownership_structure'
            ) {
              this.propertyForm.patchValue(
                {
                  [prop.property_name]: [],
                },
                { emitEvent: false },
              );
              this.updatedProperties[prop.property_name] = [];
              if (this.tableEditData[prop.property_name]) {
                delete this.tableEditData[prop.property_name];
              }
            } else {
              this.propertyForm.patchValue(
                {
                  [prop.property_name]: '',
                },
                { emitEvent: false },
              );
              this.updatedProperties[prop.property_name] = '';
            }
          }
        });
      }

      // Check if this is capital_product_type change to only 'Grant Programs'
      if (property?.property.toLowerCase() === 'capital_product_type') {
        const capitalProductType = Array.isArray(value) ? value : [];
        const isOnlyGrantPrograms =
          capitalProductType.length === 1 &&
          (String(capitalProductType[0]).toLowerCase() === 'grant programs' ||
            String(capitalProductType[0]).toLowerCase() === 'grant_programs');

        if (isOnlyGrantPrograms) {
          // Clear will_fill_prescreening
          const willFillPrescreeningProperty =
            this.recordDetails?.detail.properties.find(
              (p) => p.property.toLowerCase() === 'will_fill_prescreening',
            );
          if (willFillPrescreeningProperty) {
            this.propertyForm.patchValue(
              {
                [willFillPrescreeningProperty.property_name]: '',
              },
              { emitEvent: false },
            );
            this.updatedProperties[willFillPrescreeningProperty.property_name] =
              '';
          }

          // Clear all prescreening-related properties (both basic and detailed)
          const allPrescreeningProperties = [
            'credit_score_range',
            'has_co_signer',
            'has_business_plan',
            'has_financial_projections',
            'project_description',
            'contributing_personal_capital',
            'personal_capital_percentage',
            'collateral_types',
            'reporting_period',
            'period_months',
            'net_profit',
            'depreciation_amortization',
            'total_assets',
            'cash_equivalents',
            'receivables',
            'inventory_value',
            'current_liabilities',
            'shareholder_equity',
            'alternative_net_worth',
            'total_personal_income',
            'personal_net_worth',
            'personal_loans_amount',
            'existing_loans_amount',
            'credit_lines_limit',
            'monthly_debt_service',
            'ownership_structure',
          ];

          allPrescreeningProperties.forEach((propKey) => {
            const prop = this.recordDetails?.detail.properties.find(
              (p) => p.property.toLowerCase() === propKey,
            );
            if (prop) {
              // Handle different field types appropriately
              if (
                prop.type === 'multiselect' ||
                prop.type === 'list' ||
                prop.type === 'table' ||
                propKey === 'ownership_structure'
              ) {
                this.propertyForm.patchValue(
                  {
                    [prop.property_name]: [],
                  },
                  { emitEvent: false },
                );
                this.updatedProperties[prop.property_name] = [];
                if (this.tableEditData[prop.property_name]) {
                  delete this.tableEditData[prop.property_name];
                }
              } else {
                this.propertyForm.patchValue(
                  {
                    [prop.property_name]: '',
                  },
                  { emitEvent: false },
                );
                this.updatedProperties[prop.property_name] = '';
              }
            }
          });
        }
      }

      if (property?.property === 'lead_info::lead_status') {
        const statusLabel =
          this.getOptionLabel(property.options, value) || value;
        const isFunded = this.fundedStatuses.includes(String(statusLabel));

        if (isFunded) {
          const approvedProperty = this.recordDetails?.detail.properties.find(
            (p) => p.property === 'lead_info::approved_loan_amount',
          );
          if (approvedProperty && this.originalApprovedAmount !== null) {
            // Explicitly ensure the amount is in the payload when switching statuses
            this.updatedProperties[approvedProperty.property_name] =
              this.originalApprovedAmount;
          }
        } else {
          // If changing to NON-funded, clear the amount from payload
          const approvedProperty = this.recordDetails?.detail.properties.find(
            (p) => p.property === 'lead_info::approved_loan_amount',
          );
          if (approvedProperty) {
            this.updatedProperties[approvedProperty.property_name] = '';
          }
        }
      }

      this.updatedProperties[propertyName] = value;
      this.isDirty = true;

      // City and Zip Code in the payload
      if (property?.property.toLowerCase() === 'zip_code') {
        const cityProp = this.recordDetails?.detail.properties.find(
          (p) => p.property.toLowerCase() === 'city',
        );
        const zipProp = this.recordDetails?.detail.properties.find(
          (p) => p.property.toLowerCase() === 'zip_code',
        );

        if (cityProp && zipProp) {
          const cityValue = this.propertyForm.get(
            cityProp.property_name,
          )?.value;
          const zipValue = this.propertyForm.get(zipProp.property_name)?.value;

          // Ensure both are added to the payload whenever either one changes
          this.updatedProperties[cityProp.property_name] = cityValue;
          this.updatedProperties[zipProp.property_name] = zipValue;
        }
      }
    }, 0);
  }

  showDetailsUpdatedToast() {
    this.isEditClicked = false;
    this.checkEditDelete();
    this.commonService.triggerToast({
      type: ToastType.Success,
      message: 'Details updated successfully!',
    });
  }

  private isFundedStatusSelected(): boolean {
    const leadStatusProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property === 'lead_info::lead_status',
    );

    if (!leadStatusProperty) return false;

    const value =
      this.propertyForm.get(leadStatusProperty.property_name)?.value ??
      leadStatusProperty.value;

    const label =
      this.getOptionLabel(leadStatusProperty.options, value) || value;

    return this.fundedStatuses.includes(String(label));
  }

  private isApprovedAmountMissing(): boolean {
    const approvedProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property === 'lead_info::approved_loan_amount',
    );

    if (!approvedProperty) return false;

    const value =
      this.propertyForm.get(approvedProperty.property_name)?.value ??
      approvedProperty.value;

    return value === null || value === '' || value === undefined;
  }

  isFundedWithoutAmount(): boolean {
    return this.isFundedStatusSelected() && this.isApprovedAmountMissing();
  }

  getApprovedAmountError(): string | null {
    if (!this.isEditClicked) {
      return null;
    }

    const leadStatusProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property === 'lead_info::lead_status',
    );

    if (!leadStatusProperty) {
      return null;
    }

    const statusValue =
      this.propertyForm.get(leadStatusProperty.property_name)?.value ??
      leadStatusProperty.value;

    const statusLabel =
      this.getOptionLabel(leadStatusProperty.options, statusValue) ||
      statusValue;

    const fundedStatusSet = new Set<string>([
      'Active Funded',
      'Active Funded Grant',
    ]);

    if (!fundedStatusSet.has(String(statusLabel))) {
      return null;
    }

    const approvedProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property === 'lead_info::approved_loan_amount',
    );

    if (!approvedProperty) return null;

    const control = this.propertyForm.get(approvedProperty.property_name);
    const value = control?.value ?? approvedProperty.value;

    if (value === null || value === undefined || String(value).trim() === '') {
      return this.isActiveFundedGrantLeadStatus()
        ? 'Approved grant amount is required.'
        : 'Approved loan amount is required.';
    }

    const num = Number(value);

    if (isNaN(num)) {
      return 'Invalid number.';
    }

    if (num <= 0) {
      return 'Amount must be greater than 0.';
    }

    const str = String(value);
    const parts = str.split('.');
    const integerPart = parts[0].replace(/^-/, '');
    const decimalPart = parts[1] || '';

    if (integerPart.length > 10) {
      return this.isActiveFundedGrantLeadStatus()
        ? 'Approved grant amount must not exceed 10 whole digits.'
        : 'Approved loan amount must not exceed 10 whole digits.';
    }

    if (decimalPart.length > 2) {
      return this.isActiveFundedGrantLeadStatus()
        ? 'Approved grant amount must not exceed 2 decimal places.'
        : 'Approved loan amount must not exceed 2 decimal places.';
    }

    return null;
  }

  getPropertyValidators(prop: Properties): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    if (prop.isRequired) {
      validators.push(Validators.required);
    }
    if (prop.type === 'number') {
      validators.push(
        this.numberValidatorService.numberValidator(prop as NumberConstraints),
      );
    }
    return validators;
  }

  getNumberPlaceholder(prop: Properties): string {
    if (
      prop.property === 'lead_info::approved_loan_amount' ||
      prop.property === 'lead_info::approved_grant_amount'
    ) {
      return '0.00';
    }
    if (prop.type !== 'number') {
      return '';
    }

    if (
      prop.decimal_places !== undefined &&
      prop.decimal_places !== null &&
      prop.decimal_places > 0
    ) {
      return `0.${'0'.repeat(prop.decimal_places)}`;
    }

    return '0';
  }

  getPropertyError(prop: Properties): string {
    const control = this.propertyForm.get(prop.property_name);
    if (!control || !control.errors || (!control.touched && !control.dirty)) {
      return '';
    }
    if (control.errors['required']) {
      return `${prop.property_name} is required`;
    }
    if (control.errors['min']) {
      return `Value must be at least ${control.errors['min'].min}`;
    }
    if (control.errors['max']) {
      return `Value must be at most ${control.errors['max'].max}`;
    }
    if (control.errors['pattern']) {
      if (prop.decimal_places === 0) {
        return `Value must be an integer`;
      }
      return `Invalid format`;
    }
    if (control.errors['invalidNumber']) {
      return `Invalid number`;
    }
    if (control.errors['decimalPlaces']) {
      return `Exceeds ${prop.decimal_places} decimal place(s)`;
    }
    if (control.errors['maxDigits']) {
      const err = control.errors['maxDigits'];
      if (err.isWholeDigits) {
        return `Must not exceed ${err.maxWholeDigits} whole number digit(s)`;
      }
      if (prop.decimal_places === 0) {
        return `Must not exceed ${prop.max_digits} digit(s)`;
      }
      return `Must not exceed ${prop.max_digits} total digit(s)`;
    }
    return '';
  }

  hasApprovedAmountErrors(): boolean {
    // Existing logic (missing check)
    if (this.isFundedWithoutAmount()) {
      return true;
    }

    // Full validation (0, negative, invalid, etc.)
    return !!this.getApprovedAmountError();
  }

  primaryActionClicked(): void {
    if (this.primaryAction === 'Edit') {
      this.initializeForm(this.recordDetails!);

      const approvedProperty = this.recordDetails?.detail.properties.find(
        (p) => p.property === 'lead_info::approved_loan_amount',
      );

      if (approvedProperty) {
        this.originalApprovedAmount = approvedProperty.value ?? null;
      }

      this.isEditClicked = true;
      this.primaryAction = 'Save';
      this.secondaryAction = 'Cancel';
      this.hideSecondaryButton = false;
      this.loadStates();
    } else if (this.primaryAction === 'Save') {
      if (this.isFundedWithoutAmount()) {
        const approvedProperty = this.recordDetails?.detail.properties.find(
          (p) => p.property === 'lead_info::approved_loan_amount',
        );

        if (approvedProperty) {
          this.propertyForm
            .get(approvedProperty.property_name)
            ?.markAsTouched();
        }

        return;
      }
      const isLeadInfoValid =
        !this.leadInfoComponent || this.leadInfoComponent.isValid();

      if (!isLeadInfoValid) {
        return;
      }

      const isLeadInfoDirty =
        this.leadInfoComponent && this.leadInfoComponent.hasChanges();
      if (isLeadInfoDirty) {
        this.leadInfoComponent.saveLeadAttributes();
      }

      if (this.isDirty) {
        this.saveCompanyDetails();
      }

      if (!this.isDirty) {
        this.isEditClicked = false;
        this.checkEditDelete();
      }

      if (!isLeadInfoDirty && !this.isDirty) {
        this.commonService.triggerToast({
          type: ToastType.Success,
          message: 'Details updated successfully!',
        });
      }
    } else if (this.primaryAction === 'Delete') {
      this.showDeletePopup = true;
    }
  }

  secondaryActionClicked(): void {
    if (this.secondaryAction === 'Cancel') {
      this.isEditClicked = false;
      this.checkEditDelete();
      this.isDirty = false;
      this.updatedProperties = {};
      this.tableEditData = {}; // Clear table edit data
      this.showLoanTypeChangeWarning = false;
      if (this.leadInfoComponent) {
        this.leadInfoComponent.leadAttributes =
          this.leadInfoComponent.originalLeadAttributes;
      }
      this.applicationSummary = this.originalApplicationSummary;
      // Also restore the property value in recordDetails
      const applicationSummaryProperty =
        this.recordDetails?.detail.properties.find(
          (prop) => prop.property === 'application_summary',
        );
      if (applicationSummaryProperty) {
        applicationSummaryProperty.value =
          this.originalApplicationSummary ?? '';
      }
    } else if (this.secondaryAction === 'Delete') {
      this.showDeletePopup = true;
    } else if (this.secondaryAction === 'Close') {
      this.closePopup(false);
    }
  }

  onLeadInfoStatusUpdated(event: {
    uniqueId: string;
    newStatus: string;
  }): void {
    if (this.recordDetails?.lead_attributes) {
      this.recordDetails.lead_attributes['lead_status'] = Number(
        event.newStatus,
      );
    }
    this.leadStatusUpdated.emit(event);
  }

  onLeadStatusChanged(event: {
    uniqueId: string;
    newStatus: string;
    previousStatus?: string;
    approvedAmount?: number | null;
  }): void {
    const newStatusNum = Number(event.newStatus);

    if (this.recordDetails?.lead_attributes) {
      this.recordDetails.lead_attributes['lead_status'] = newStatusNum;
      if (event.approvedAmount !== undefined) {
        this.recordDetails.lead_attributes['approved_loan_amount'] =
          event.approvedAmount;
      }
    }
    if (this.company) {
      this.company.lead_status = event.newStatus;
      if (this.company.lead_attributes) {
        this.company.lead_attributes['lead_status'] = newStatusNum;
        if (event.approvedAmount !== undefined) {
          this.company.lead_attributes['approved_loan_amount'] =
            event.approvedAmount;
        }
      }
    }

    const statusProp = this.recordDetails?.detail?.properties?.find(
      (p) =>
        p.property === 'lead_info::lead_status' ||
        p.property_name?.toLowerCase() === 'lead status',
    );
    if (statusProp) {
      statusProp.value = event.newStatus;
      if (
        statusProp.property_name &&
        this.propertyForm?.get(statusProp.property_name)
      ) {
        this.propertyForm
          .get(statusProp.property_name)
          ?.setValue(event.newStatus);
      }
    }

    const approvedProp = this.recordDetails?.detail?.properties?.find(
      (p) =>
        p.property === 'lead_info::approved_loan_amount' ||
        p.property_name?.toLowerCase() === 'approved loan amount',
    );
    if (approvedProp && event.approvedAmount !== undefined) {
      approvedProp.value =
        event.approvedAmount != null ? String(event.approvedAmount) : '';
      if (
        approvedProp.property_name &&
        this.propertyForm?.get(approvedProp.property_name)
      ) {
        this.propertyForm
          .get(approvedProp.property_name)
          ?.setValue(event.approvedAmount);
      }
    }

    if (this.recordDetails?.assignees) {
      for (const a of this.recordDetails.assignees) {
        a.lead_status = newStatusNum;
      }
    }
    if (this.company?.assignees) {
      for (const a of this.company.assignees) {
        a.lead_status = newStatusNum;
      }
    }

    this.leadStatusUpdated.emit({
      uniqueId: event.uniqueId,
      newStatus: event.newStatus,
    });
  }

  saveCompanyDetails(): void {
    if (!this.applicationSummary?.trim()) {
      this.commonService.triggerToast({
        type: ToastType.Error,
        message: 'Please fill in all required fields!',
      });
      return;
    }
    // Validate the form
    if (this.propertyForm.invalid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.propertyForm.controls).forEach((key) => {
        this.propertyForm.get(key)?.markAsTouched();
      });
      this.commonService.triggerToast({
        type: ToastType.Error,
        message: 'Please fill in all required fields!',
      });

      return;
    }

    if (!this.isDirty) {
      this.showDetailsUpdatedToast();
      return;
    }

    this.auditLogService.logFeature({
      featureName: 'edit_save_lead_info',
      action: 'save',
      meta: {
        recordId: this.company.unique_id,
        recordName: this.company.company_name,
      },
      description: 'User edits and saves lead info',
    });

    const hasUpdatedProperty = (propertyName: string): boolean =>
      Object.prototype.hasOwnProperty.call(
        this.updatedProperties,
        propertyName,
      );

    // Clear personal_capital_percentage if contributing_personal_capital is No
    const contributingProperty = this.recordDetails!.detail.properties.find(
      (p) => p.property.toLowerCase() === 'contributing_personal_capital',
    );
    const contributingValue = contributingProperty
      ? this.propertyForm.get(contributingProperty.property_name)?.value
      : null;
    const contributingWasEdited = contributingProperty
      ? hasUpdatedProperty(contributingProperty.property_name)
      : false;

    if (contributingWasEdited && contributingValue === 'No') {
      const percentageProperty = this.recordDetails!.detail.properties.find(
        (p) => p.property.toLowerCase() === 'personal_capital_percentage',
      );
      if (percentageProperty) {
        // Set to empty string, will be converted to null in payload
        this.updatedProperties[percentageProperty.property_name] = '';
      }
    }

    // Clear custom_months if period_months is not Other
    const periodMonthsProperty = this.recordDetails!.detail.properties.find(
      (p) => p.property.toLowerCase() === 'period_months',
    );
    const periodMonthsValue = periodMonthsProperty
      ? this.propertyForm.get(periodMonthsProperty.property_name)?.value
      : null;
    const periodMonthsWasEdited = periodMonthsProperty
      ? hasUpdatedProperty(periodMonthsProperty.property_name)
      : false;

    if (
      periodMonthsWasEdited &&
      periodMonthsValue &&
      periodMonthsValue !== 'Other'
    ) {
      const customMonthsProperty = this.recordDetails!.detail.properties.find(
        (p) => p.property.toLowerCase() === 'custom_months',
      );
      if (customMonthsProperty) {
        // Set to empty string, will be converted to null in payload
        this.updatedProperties[customMonthsProperty.property_name] = '';
      }
    }

    // Clear succession-related properties if Business Succession is removed from business_help_aspects
    const businessHelpAspectsProperty =
      this.recordDetails!.detail.properties.find(
        (p) => p.property.toLowerCase() === 'business_help_aspects',
      );
    const businessHelpAspectsValue = businessHelpAspectsProperty
      ? this.propertyForm.get(businessHelpAspectsProperty.property_name)?.value
      : null;
    const businessHelpAspectsWasEdited = businessHelpAspectsProperty
      ? hasUpdatedProperty(businessHelpAspectsProperty.property_name)
      : false;

    const hasBusinessSuccession =
      Array.isArray(businessHelpAspectsValue) &&
      businessHelpAspectsValue.some(
        (aspect) =>
          String(aspect).toLowerCase() === 'business succession' ||
          String(aspect).toLowerCase() === 'business_succession',
      );

    if (businessHelpAspectsWasEdited && !hasBusinessSuccession) {
      const successionProperties = [
        'interested_in_selling_business',
        'interested_in_employee_strategies',
        'interested_in_succession_planning',
      ];

      successionProperties.forEach((propName) => {
        const prop = this.recordDetails!.detail.properties.find(
          (p) => p.property.toLowerCase() === propName,
        );
        if (prop) {
          // Set to empty string, will be converted to null in payload
          this.updatedProperties[prop.property_name] = '';
        }
      });
    }

    // Clear properties from groups that are no longer selected in Interested Products
    const interestedProductsProperty = this.findInterestedProductsProperty(
      this.recordDetails!.detail.properties,
    );

    if (
      interestedProductsProperty &&
      hasUpdatedProperty(interestedProductsProperty.property_name)
    ) {
      const currentProducts =
        this.propertyForm.get(interestedProductsProperty.property_name)
          ?.value || [];
      const originalProducts = interestedProductsProperty.value || [];

      // Map labels to option values and vice versa
      const labelToOptionValueMap: Record<string, string> = {
        'Loans and Capital': 'loans_and_capital',
        'Technical Assistance': 'technical_assistance',
        'Business Operational Tools': 'business_operational_tools',
        'Commercial Real Estate': 'commercial_real_estate',
        'Solar and Energy': 'solar_and_energy',
        'Finding Investors': 'finding_investors',
      };

      const optionValueToLabelMap: Record<string, string> = {
        loans_and_capital: 'Loans and Capital',
        technical_assistance: 'Technical Assistance',
        business_operational_tools: 'Business Operational Tools',
        commercial_real_estate: 'Commercial Real Estate',
        solar_and_energy: 'Solar and Energy',
        finding_investors: 'Finding Investors',
      };

      // Normalize both arrays to option values for comparison
      const normalizedOriginal = Array.isArray(originalProducts)
        ? originalProducts.map(
            (product: string) => labelToOptionValueMap[product] || product,
          )
        : [];

      const normalizedCurrent = Array.isArray(currentProducts)
        ? currentProducts
        : [];

      // Find products that were removed (in option value format)
      const removedProducts = normalizedOriginal.filter(
        (product: string) => !normalizedCurrent.includes(product),
      );

      if (removedProducts.length > 0) {
        // Map product labels to group names
        const productToGroupMap: Record<string, string> = {
          'Loans and Capital': 'Loans & Capital',
          'Technical Assistance': 'Technical Assistance',
          'Business Operational Tools': 'Business Operational Tools',
          'Commercial Real Estate': 'Commercial Real Estate',
          'Solar and Energy': 'Solar & Energy',
          'Finding Investors': 'Finding Investors',
        };

        // Get the groups that should be cleared (convert option values back to labels first)
        const groupsToClear = removedProducts
          .map((product: string) => {
            const label = optionValueToLabelMap[product] || product;
            return productToGroupMap[label] || label;
          })
          .filter((group: string) => group);

        // Clear all properties from the removed groups
        this.recordDetails!.detail.properties.forEach((prop) => {
          if (prop.group && groupsToClear.includes(prop.group)) {
            // Skip the interested_products field itself
            if (this.isInterestedProductsProperty(prop)) {
              return;
            }

            // Add to updated properties with appropriate empty value
            if (prop.type === 'multiselect' || prop.type === 'list') {
              this.updatedProperties[prop.property_name] = [];
            } else if (prop.type === 'table') {
              this.updatedProperties[prop.property_name] = [];
            } else {
              // Set to empty string, will be converted to null in payload
              this.updatedProperties[prop.property_name] = '';
            }
          }
        });
      }
    }

    // Inject lead_status automatically if approved_loan_amount is updated
    const approvedAmountProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property === 'lead_info::approved_loan_amount',
    );

    const leadStatusProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property === 'lead_info::lead_status',
    );

    if (leadStatusProperty && approvedAmountProperty) {
      const approvedAmountUpdated = Object.prototype.hasOwnProperty.call(
        this.updatedProperties,
        approvedAmountProperty.property_name,
      );

      const statusUpdated = Object.prototype.hasOwnProperty.call(
        this.updatedProperties,
        leadStatusProperty.property_name,
      );

      const finalStatus =
        this.propertyForm.get(leadStatusProperty.property_name)?.value ??
        leadStatusProperty.value;

      const label =
        this.getOptionLabel(leadStatusProperty.options, finalStatus) ||
        finalStatus;

      const isFunded = this.fundedStatuses.includes(label);

      // Status changed ? NOT funded ? send NULL (NOT delete)
      if (statusUpdated && !isFunded) {
        this.updatedProperties[approvedAmountProperty.property_name] = null;
      }

      // User updated amount
      else if (approvedAmountUpdated) {
        const approvedValue = this.propertyForm.get(
          approvedAmountProperty.property_name,
        )?.value;

        if (isFunded) {
          if (
            approvedValue !== null &&
            approvedValue !== '' &&
            Number(approvedValue) > 0
          ) {
            this.updatedProperties[approvedAmountProperty.property_name] =
              approvedValue;
          }

          // ALWAYS include status when amount updated
          this.updatedProperties[leadStatusProperty.property_name] =
            finalStatus;
        } else {
          // amount updated but NOT funded ? send null
          this.updatedProperties[approvedAmountProperty.property_name] = null;

          // also include status
          this.updatedProperties[leadStatusProperty.property_name] =
            finalStatus;
        }
      } else if (statusUpdated && isFunded) {
        const approvedValue =
          this.propertyForm.get(approvedAmountProperty.property_name)?.value ??
          approvedAmountProperty.value;

        if (
          approvedValue !== null &&
          approvedValue !== '' &&
          Number(approvedValue) > 0
        ) {
          this.updatedProperties[approvedAmountProperty.property_name] =
            approvedValue;
        }
        this.updatedProperties[leadStatusProperty.property_name] = finalStatus;
      }
    }

    const payload = {
      unique_id: this.company.unique_id,
      properties: Object.entries(this.updatedProperties)
        .filter(([property_name]) => {
          const lowerName = property_name.toLowerCase();
          return lowerName !== 'latitude' && lowerName !== 'longitude';
        })
        .map(([property_name, value]) => {
          // Find the corresponding property from record details
          const propertyDetail = this.recordDetails!.detail.properties.find(
            (p) => p.property_name === property_name,
          );
          const propertyKey = propertyDetail?.property || property_name;

          // Convert table / list / multiselect / ownership_structure to array (never empty string or null)
          if (
            propertyDetail?.type === 'table' ||
            propertyDetail?.type === 'list' ||
            propertyDetail?.type === 'multiselect' ||
            propertyKey.toLowerCase().includes('ownership_structure') ||
            property_name.toLowerCase().includes('ownership_structure')
          ) {
            return {
              property: propertyKey,
              value: Array.isArray(value) ? value : [],
            };
          }

          // Convert number type properties to numbers or null if empty
          if (propertyDetail?.type === 'number') {
            const numValue =
              value === '' || value === null ? null : Number(value);
            return {
              property: propertyKey,
              value: numValue === null || isNaN(numValue) ? null : numValue,
            };
          }

          // Convert dropdown values to their correct type (number, string, boolean)
          if (propertyDetail?.type === 'dropdown' && propertyDetail.options) {
            // If value is empty string, convert to null
            if (value === '' || value === null) {
              return {
                property: propertyKey,
                value: null,
              };
            }

            // Find the option value that matches (handles both string and already-converted values)
            const optionEntry = Object.entries(propertyDetail.options).find(
              ([, optionValue]) => String(optionValue) === String(value),
            );

            if (optionEntry) {
              const [, optionValue] = optionEntry;
              // Return the original option value (preserves number, string, or boolean type)
              return {
                property: propertyKey,
                value: optionValue,
              };
            }
          }

          // Convert radio button values to their correct type (number, string, boolean)
          if (propertyDetail?.type === 'radio' && propertyDetail.options) {
            // If value is empty string, convert to null
            if (value === '' || value === null) {
              return {
                property: propertyKey,
                value: null,
              };
            }

            // Find the option value that matches (handles both string and already-converted values)
            const optionEntry = Object.entries(propertyDetail.options).find(
              ([, optionValue]) => String(optionValue) === String(value),
            );

            if (optionEntry) {
              const [, optionValue] = optionEntry;
              // Return the original option value (preserves number, string, or boolean type)
              return {
                property: propertyKey,
                value: optionValue,
              };
            }
          }

          return {
            property: propertyKey,
            value,
          };
        }),
    };

    this.searchService
      .updateCompanyDetails(payload, this.company.source)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: CompanyDetails) => {
          this.recordDetails = res;

          this.checkEditDelete();

          // Re-check if score should be shown based on updated data
          if (this.isLoanFormFilled()) {
            this.canShowScore = true;
          } else {
            this.canShowScore = false;
          }

          // Update matched_record_connections if present in response
          if (res.matched_record_connections !== undefined) {
            this.recordDetails.matched_record_connections =
              res.matched_record_connections;
          }

          // Check for company name update
          const nameProperty = res.detail.properties.find(
            (p) => p.property.toLowerCase() === 'company_name',
          );
          if (nameProperty) {
            this.company.company_name = nameProperty.value;
            this.title = nameProperty.value; // Update title if company name changes
          }

          // Check for city and state updates and update short_address
          const cityProperty = res.detail.properties.find(
            (p) => p.property.toLowerCase() === 'city',
          );
          const stateProperty = res.detail.properties.find(
            (p) => p.property.toLowerCase() === 'state',
          );

          if (cityProperty || stateProperty) {
            const city =
              cityProperty?.value || this.company.short_address.split(',')[0];
            const state =
              stateProperty?.value ||
              this.company.short_address.split(',')[1]?.trim();
            this.company.short_address = `${city}, ${state}`;
          }

          // Check for lat and lon to update location pin
          const lat = res.latitude;
          if (lat) {
            this.company.latitude = lat;
          }

          const lon = res.longitude;
          if (lon) {
            this.company.longitude = lon;
          }

          // Only emit if there were changes to name or address
          if (nameProperty || cityProperty || stateProperty) {
            this.companyDetailsUpdated.emit(this.company);
          }

          this.showDetailsUpdatedToast();
          this.isDirty = false;
          this.updatedProperties = {};
          this.tableEditData = {}; // Clear table edit data
        },
        error: (err) => {
          console.log(err);
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: err.error.detail ?? 'Failed to update details!',
          });
        },
      });
  }

  isDropdown(property: Properties): boolean {
    return (
      property.type === 'dropdown' &&
      property.options !== null &&
      typeof property.options === 'object'
    );
  }

  isMultiselect(property: Properties): boolean {
    return (
      property.type === 'multiselect' &&
      property.options !== null &&
      typeof property.options === 'object'
    );
  }

  // Helper methods for working with new options structure
  getOptionKeys(
    options: Record<string, string | number | boolean> | null,
  ): string[] {
    if (!options || typeof options !== 'object') {
      return [];
    }
    return Object.keys(options);
  }

  getOptionValue(
    options: Record<string, string | number | boolean> | null,
    label: string,
  ): string | number | boolean | null {
    if (!options || typeof options !== 'object') {
      return null;
    }
    return options[label] ?? null;
  }

  getOptionLabel(
    options: Record<string, string | number | boolean> | null,
    value: string | number | boolean,
  ): string | null {
    if (
      !options ||
      typeof options !== 'object' ||
      value === null ||
      value === undefined
    ) {
      return null;
    }
    // Find the key (label) that matches the value
    const entry = Object.entries(options).find(([, optionValue]) => {
      // Handle string comparison - convert both to strings for comparison
      return String(optionValue) === String(value);
    });
    return entry ? entry[0] : null;
  }

  isStateProperty(property: Properties): boolean {
    return property.property_name.toLowerCase() === 'state';
  }

  isCountyProperty(property: Properties): boolean {
    return property.property_name.toLowerCase() === 'county';
  }

  shouldHideProperty(property: Properties): boolean {
    if (
      property.property.toLowerCase() === 'latitude' ||
      property.property.toLowerCase() === 'longitude'
    ) {
      return true;
    }

    // Hide will_fill_prescreening and all related fields when capital_product_type is only 'Grant Programs'
    const capitalProductType = this.getCapitalProductTypeValue();
    const isOnlyGrantPrograms =
      Array.isArray(capitalProductType) &&
      capitalProductType.length === 1 &&
      (String(capitalProductType[0]).toLowerCase() === 'grant programs' ||
        String(capitalProductType[0]).toLowerCase() === 'grant_programs');

    if (isOnlyGrantPrograms) {
      // Hide will_fill_prescreening itself
      if (property.property.toLowerCase() === 'will_fill_prescreening') {
        return true;
      }

      // Hide basic prescreening properties
      if (
        property.property.toLowerCase() === 'credit_score_range' ||
        property.property.toLowerCase() === 'has_co_signer' ||
        property.property.toLowerCase() === 'has_business_plan' ||
        property.property.toLowerCase() === 'has_financial_projections'
      ) {
        return true;
      }

      // Hide detailed loan properties
      if (
        property.property.toLowerCase() === 'project_description' ||
        property.property.toLowerCase() === 'contributing_personal_capital' ||
        property.property.toLowerCase() === 'personal_capital_percentage' ||
        property.property.toLowerCase() === 'collateral_types' ||
        property.property.toLowerCase() === 'reporting_period' ||
        property.property.toLowerCase() === 'period_months' ||
        property.property.toLowerCase() === 'net_profit' ||
        property.property.toLowerCase() === 'depreciation_amortization' ||
        property.property.toLowerCase() === 'total_assets' ||
        property.property.toLowerCase() === 'cash_equivalents' ||
        property.property.toLowerCase() === 'receivables' ||
        property.property.toLowerCase() === 'inventory_value' ||
        property.property.toLowerCase() === 'current_liabilities' ||
        property.property.toLowerCase() === 'shareholder_equity' ||
        property.property.toLowerCase() === 'alternative_net_worth' ||
        property.property.toLowerCase() === 'total_personal_income' ||
        property.property.toLowerCase() === 'personal_net_worth' ||
        property.property.toLowerCase() === 'personal_loans_amount' ||
        property.property.toLowerCase() === 'existing_loans_amount' ||
        property.property.toLowerCase() === 'credit_lines_limit' ||
        property.property.toLowerCase() === 'monthly_debt_service' ||
        property.property.toLowerCase() === 'ownership_structure'
      ) {
        return true;
      }
    }

    // Hide loan-related properties when loan_type is Working Capital Loan
    if (this.loanRelatedProperties.includes(property.property.toLowerCase())) {
      const currentLoanType = this.getCurrentLoanTypeValue();
      if (currentLoanType === 'Working Capital Loan') {
        return true;
      }
    }

    // Hide personal_capital_percentage when contributing_personal_capital is No (false)
    if (property.property.toLowerCase() === 'personal_capital_percentage') {
      const contributingCapitalValue =
        this.getContributingPersonalCapitalValue();
      if (
        contributingCapitalValue === false ||
        contributingCapitalValue === 'No'
      ) {
        return true;
      }
    }

    // Hide custom_months when period_months is not Other
    if (property.property.toLowerCase() === 'custom_months') {
      const periodMonthsValue = this.getPeriodMonthsValue();
      if (periodMonthsValue !== 'Other') {
        return true;
      }
    }

    // Hide investment_types when knows_investor_type is not 'yes'
    if (property.property.toLowerCase() === 'investment_types') {
      const knowsInvestorTypeValue =
        this.getKnowsInvestorTypeValue()?.toLowerCase();
      if (knowsInvestorTypeValue !== 'yes') {
        return true;
      }
    }

    // Hide own_facility when looking_to_upgrade_facility is not 'yes' or true
    if (property.property.toLowerCase() === 'own_facility') {
      const lookingToUpgradeValue = this.getLookingToUpgradeFacilityValue();
      if (lookingToUpgradeValue !== true && lookingToUpgradeValue !== 'Yes') {
        return true;
      }
    }

    // Hide building_owner_willing_to_support when own_facility is not 'No' or false
    if (
      property.property.toLowerCase() === 'building_owner_willing_to_support'
    ) {
      const ownFacilityValue = this.getOwnFacilityValue();
      if (ownFacilityValue !== false && ownFacilityValue !== 'No') {
        return true;
      }
    }

    // Hide wants_startup_assistance and startup_help_areas when source_type is CUSTOM
    if (
      property.property.toLowerCase() === 'wants_startup_assistance' ||
      property.property.toLowerCase() === 'startup_help_areas'
    ) {
      if (this.recordDetails?.source_type === 'CUSTOM') {
        return true;
      }
    }

    // Hide succession-related properties when 'Business Succession' is not in business_help_aspects
    if (
      property.property.toLowerCase() === 'interested_in_selling_business' ||
      property.property.toLowerCase() === 'interested_in_employee_strategies' ||
      property.property.toLowerCase() === 'interested_in_succession_planning'
    ) {
      const businessHelpAspects = this.getBusinessHelpAspectsValue();
      if (!businessHelpAspects || !Array.isArray(businessHelpAspects)) {
        return true;
      }
      // Check if 'Business Succession' is in the array (could be label or option value)
      const hasBusinessSuccession = businessHelpAspects.some(
        (aspect) =>
          String(aspect).toLowerCase() === 'business succession' ||
          String(aspect).toLowerCase() === 'business_succession',
      );
      if (!hasBusinessSuccession) {
        return true;
      }
    }

    // Hide prescreening-related properties when will_fill_prescreening is not 'No' or false
    if (
      property.property.toLowerCase() === 'credit_score_range' ||
      property.property.toLowerCase() === 'has_co_signer' ||
      property.property.toLowerCase() === 'has_business_plan' ||
      property.property.toLowerCase() === 'has_financial_projections'
    ) {
      const willFillPrescreening = this.getWillFillPrescreeningValue();
      if (willFillPrescreening !== false && willFillPrescreening !== 'No') {
        return true;
      }
    }

    // Hide detailed loan properties when will_fill_prescreening is 'No' or false
    if (
      property.property.toLowerCase() === 'project_description' ||
      property.property.toLowerCase() === 'contributing_personal_capital' ||
      property.property.toLowerCase() === 'personal_capital_percentage' ||
      property.property.toLowerCase() === 'collateral_types' ||
      property.property.toLowerCase() === 'reporting_period' ||
      property.property.toLowerCase() === 'period_months' ||
      property.property.toLowerCase() === 'net_profit' ||
      property.property.toLowerCase() === 'depreciation_amortization' ||
      property.property.toLowerCase() === 'total_assets' ||
      property.property.toLowerCase() === 'cash_equivalents' ||
      property.property.toLowerCase() === 'receivables' ||
      property.property.toLowerCase() === 'inventory_value' ||
      property.property.toLowerCase() === 'current_liabilities' ||
      property.property.toLowerCase() === 'shareholder_equity' ||
      property.property.toLowerCase() === 'alternative_net_worth' ||
      property.property.toLowerCase() === 'total_personal_income' ||
      property.property.toLowerCase() === 'personal_net_worth' ||
      property.property.toLowerCase() === 'personal_loans_amount' ||
      property.property.toLowerCase() === 'existing_loans_amount' ||
      property.property.toLowerCase() === 'credit_lines_limit' ||
      property.property.toLowerCase() === 'monthly_debt_service' ||
      property.property.toLowerCase() === 'ownership_structure'
    ) {
      const willFillPrescreening = this.getWillFillPrescreeningValue();
      if (willFillPrescreening === false || willFillPrescreening === 'No') {
        return true;
      }
    }

    // Hide Approved Loan Amount unless status is Funded
    if (property.property === 'lead_info::approved_loan_amount') {
      return !this.isFundedLeadStatus();
    }

    return false;
  }

  getContributingPersonalCapitalValue(): string | boolean | null {
    // Get current value from form if in edit mode
    const contributingProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property.toLowerCase() === 'contributing_personal_capital',
    );
    if (contributingProperty && this.isEditClicked) {
      return (
        this.propertyForm.get(contributingProperty.property_name)?.value ?? null
      );
    }
    // Otherwise get from record details
    return contributingProperty?.value ?? null;
  }

  getPeriodMonthsValue(): string | null {
    // Get current value from form if in edit mode
    const periodMonthsProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property.toLowerCase() === 'period_months',
    );
    if (periodMonthsProperty && this.isEditClicked) {
      return (
        this.propertyForm.get(periodMonthsProperty.property_name)?.value || null
      );
    }
    // Otherwise get from record details
    return periodMonthsProperty?.value || null;
  }

  getCurrentLoanTypeValue(): string | null {
    // Get current value from form if in edit mode
    const loanTypeProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property.toLowerCase() === 'loan_type',
    );
    if (loanTypeProperty && this.isEditClicked) {
      return (
        this.propertyForm.get(loanTypeProperty.property_name)?.value || null
      );
    }
    // Otherwise get from record details
    return loanTypeProperty?.value || null;
  }

  getKnowsInvestorTypeValue(): string | null {
    // Get current value from form if in edit mode
    const knowsInvestorTypeProperty =
      this.recordDetails?.detail.properties.find(
        (p) => p.property.toLowerCase() === 'knows_investor_type',
      );
    if (knowsInvestorTypeProperty && this.isEditClicked) {
      return (
        this.propertyForm.get(knowsInvestorTypeProperty.property_name)?.value ||
        null
      );
    }
    // Otherwise get from record details
    return knowsInvestorTypeProperty?.value || null;
  }

  getLookingToUpgradeFacilityValue(): string | boolean | null {
    // Get current value from form if in edit mode
    const lookingToUpgradeProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property.toLowerCase() === 'looking_to_upgrade_facility',
    );
    if (lookingToUpgradeProperty && this.isEditClicked) {
      return (
        this.propertyForm.get(lookingToUpgradeProperty.property_name)?.value ??
        null
      );
    }
    // Otherwise get from record details
    return lookingToUpgradeProperty?.value ?? null;
  }

  getOwnFacilityValue(): string | boolean | null {
    // Get current value from form if in edit mode
    const ownFacilityProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property.toLowerCase() === 'own_facility',
    );
    if (ownFacilityProperty && this.isEditClicked) {
      return (
        this.propertyForm.get(ownFacilityProperty.property_name)?.value ?? null
      );
    }
    // Otherwise get from record details
    return ownFacilityProperty?.value ?? null;
  }

  getBusinessHelpAspectsValue(): (string | number | boolean)[] | null {
    // Get current value from form if in edit mode
    const businessHelpAspectsProperty =
      this.recordDetails?.detail.properties.find(
        (p) => p.property.toLowerCase() === 'business_help_aspects',
      );
    if (businessHelpAspectsProperty && this.isEditClicked) {
      const value = this.propertyForm.get(
        businessHelpAspectsProperty.property_name,
      )?.value;
      return Array.isArray(value) ? value : null;
    }
    // Otherwise get from record details
    const value = businessHelpAspectsProperty?.value;
    return Array.isArray(value) ? value : null;
  }

  getWillFillPrescreeningValue(): string | boolean | null {
    // Get current value from form if in edit mode
    const willFillPrescreeningProperty =
      this.recordDetails?.detail.properties.find(
        (p) => p.property.toLowerCase() === 'will_fill_prescreening',
      );
    if (willFillPrescreeningProperty && this.isEditClicked) {
      return (
        this.propertyForm.get(willFillPrescreeningProperty.property_name)
          ?.value ?? null
      );
    }
    // Otherwise get from record details
    return willFillPrescreeningProperty?.value ?? null;
  }

  getCapitalProductTypeValue(): (string | number | boolean)[] | null {
    // Get current value from form if in edit mode
    const capitalProductTypeProperty =
      this.recordDetails?.detail.properties.find(
        (p) => p.property.toLowerCase() === 'capital_product_type',
      );
    if (capitalProductTypeProperty && this.isEditClicked) {
      const value = this.propertyForm.get(
        capitalProductTypeProperty.property_name,
      )?.value;
      return Array.isArray(value) ? value : null;
    }
    // Otherwise get from record details
    const value = capitalProductTypeProperty?.value;
    return Array.isArray(value) ? value : null;
  }

  shouldHideGroup(group: GroupedProperties): boolean {
    // First check if group is filtered by Interested Products
    if (this.allowedGroups !== null && group.group) {
      // Normalize group name for comparison (case-insensitive)
      const normalizedGroupName = group.group.toLowerCase().trim();
      const isAllowed = this.allowedGroups.some(
        (allowed) => allowed.toLowerCase().trim() === normalizedGroupName,
      );
      if (!isAllowed) {
        return true; // Hide this group
      }
    }

    // Then check if all properties in it should be hidden
    return group.properties.every((prop) => this.shouldHideProperty(prop));
  }

  initializeAllowedGroups(details: CompanyDetails): void {
    // Only apply filtering logic for custom business record source types
    // For other source types, show all groups
    if (details.source_type !== 'CUSTOM' && details.source_type !== 'SME') {
      this.allowedGroups = null;
      return;
    }

    // Find the Interested Products property
    const interestedProductsProperty = this.findInterestedProductsProperty(
      details.detail.properties,
    );

    if (!interestedProductsProperty || !interestedProductsProperty.value) {
      // If no Interested Products or no value, show all groups
      this.allowedGroups = null;
      return;
    }

    // Get the selected values (array of strings - could be labels or option values)
    const selectedProducts = Array.isArray(interestedProductsProperty.value)
      ? interestedProductsProperty.value
      : [];

    if (selectedProducts.length === 0) {
      // If nothing selected, only show basic groups
      this.allowedGroups = ['Basic Information', 'Lead Information'];
      return;
    }

    // Map option values to labels (in case API returns option values)
    const optionValueToLabelMap: Record<string, string> = {
      loans_and_capital: 'Loans and Capital',
      technical_assistance: 'Technical Assistance',
      business_operational_tools: 'Business Operational Tools',
      commercial_real_estate: 'Commercial Real Estate',
      solar_and_energy: 'Solar and Energy',
      finding_investors: 'Finding Investors',
    };

    // Map option labels to group names
    const productToGroupMap: Record<string, string> = {
      'Loans and Capital': 'Loans & Capital',
      'Loans and Capital Products': 'Loans & Capital',
      'Technical Assistance': 'Technical Assistance',
      'Small BusinessTechnical Assistance': 'Technical Assistance',
      'Business Operational Tools': 'Business Operational Tools',
      'Commercial Real Estate': 'Commercial Real Estate',
      'Solar and Energy': 'Solar & Energy',
      'Solar and Energy Efficiency Products': 'Solar & Energy',
      'Finding Investors': 'Finding Investors',
    };

    // Convert selected products to labels first (if they're option values), then to allowed groups
    this.allowedGroups = selectedProducts
      .map((product) => {
        // Convert option value to label if needed
        const label = optionValueToLabelMap[product] || product;
        // Convert label to group name
        return productToGroupMap[label] || label;
      })
      .filter((group) => group); // Remove any undefined/null values

    // Always include Basic Information and Lead Information groups
    if (!this.allowedGroups.includes('Basic Information')) {
      this.allowedGroups.push('Basic Information');
    }
    if (!this.allowedGroups.includes('Lead Information')) {
      this.allowedGroups.push('Lead Information');
    }
  }

  loadStates(): void {
    const stateProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property.toLowerCase() === 'state',
    );
    const countyProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property.toLowerCase() === 'county',
    );
    const cityProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property.toLowerCase() === 'city',
    );
    const zipProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property.toLowerCase() === 'zip_code',
    );

    const stateValue = stateProperty
      ? this.propertyForm.get(stateProperty.property_name)?.value
      : null;
    const countyValue = countyProperty
      ? this.propertyForm.get(countyProperty.property_name)?.value
      : null;
    const cityValue = cityProperty
      ? this.propertyForm.get(cityProperty.property_name)?.value
      : null;
    const zipValue = zipProperty
      ? this.propertyForm.get(zipProperty.property_name)?.value
      : null;

    // Initially disable city and zipcode controls
    this.getCityControl()?.disable();
    this.getZipcodeControl()?.disable();

    this.addressService
      .getStates()
      .pipe(
        takeUntil(this.destroy$),
        switchMap((states) => {
          this.states = states;
          this.availableStates = states.map((s) => s.name);

          if (stateValue) {
            const state = this.states.find((s) => s.name === stateValue);

            if (state) {
              return forkJoin({
                counties: this.addressService.getCounties(state.id),
                cities: this.addressService.getCities(state.id),
              }).pipe(
                switchMap(({ counties, cities }) => {
                  this.counties = counties;
                  this.availableCounties = counties.map((c) => c.name);

                  this.cities = cities;
                  this.availableCities = cities.map((c) => c.name);

                  // Enable city control when cities are loaded and state is selected
                  this.getCityControl()?.enable();

                  if (countyProperty && countyValue) {
                    const matchedCounty = counties.find(
                      (c) =>
                        c.name.toLowerCase() ===
                        String(countyValue).toLowerCase(),
                    );
                    if (matchedCounty) {
                      this.propertyForm.patchValue({
                        [countyProperty.property_name]: matchedCounty.name,
                      });
                    } else {
                      this.availableCounties.push(String(countyValue));
                    }
                  }

                  let currentCityId: number | null = null;
                  if (cityProperty && cityValue) {
                    const matchedCity = cities.find(
                      (c) =>
                        c.name.toLowerCase() ===
                        String(cityValue).toLowerCase(),
                    );
                    if (matchedCity) {
                      currentCityId = matchedCity.id;
                      this.propertyForm.patchValue({
                        [cityProperty.property_name]: matchedCity.name,
                      });
                    } else {
                      this.availableCities.push(String(cityValue));
                    }
                  }

                  if (!currentCityId) return of([]);

                  return this.addressService.getZipcodes(currentCityId);
                }),
              );
            }
          }
          return of(null);
        }),
      )
      .subscribe((zips) => {
        if (zips) {
          this.zipcodes = zips;
          this.availableZipcodes = zips.map((z) => z.code);

          // Enable zipcode control when zipcodes are loaded and city is selected
          this.getZipcodeControl()?.enable();

          if (zipValue && zipProperty) {
            const matchedZip = zips.find(
              (z) => String(z.code) === String(zipValue),
            );
            if (
              !matchedZip &&
              !this.availableZipcodes.includes(String(zipValue))
            ) {
              this.availableZipcodes.push(String(zipValue));
            }
            this.propertyForm.patchValue({
              [zipProperty.property_name]: String(zipValue),
            });
          }
        }
      });
  }

  getStates(): string[] {
    return this.availableStates;
  }

  getCounties(): string[] {
    return this.availableCounties;
  }

  onStateChange(event: Event) {
    const stateName = (event.target as HTMLSelectElement).value;

    const state = this.states.find((s) => s.name === stateName);

    // FIND THE STATE PROPERTY
    const stateProp = this.recordDetails?.detail.properties.find(
      (p) => p.property.toLowerCase() === 'state',
    );

    // UPDATE THE PAYLOAD OBJECT
    if (stateProp) {
      this.updatedProperties[stateProp.property_name] = stateName;
      this.isDirty = true;
    }

    if (!state) {
      this.availableCities = [];
      this.availableCounties = [];
      this.availableZipcodes = [];
      // Disable city and zipcode controls when no state selected
      this.getCityControl()?.disable();
      this.getZipcodeControl()?.disable();
      return;
    }

    const countyProp = this.recordDetails?.detail.properties.find(
      (p) => p.property.toLowerCase() === 'county',
    );

    const cityProp = this.recordDetails?.detail.properties.find(
      (p) => p.property.toLowerCase() === 'city',
    );

    const zipProp = this.recordDetails?.detail.properties.find(
      (p) => p.property.toLowerCase() === 'zip_code',
    );

    this.propertyForm.patchValue({
      [countyProp?.property_name || '']: '',
      [cityProp?.property_name || '']: '',
      [zipProp?.property_name || '']: '',
    });

    // Disable city and zipcode while loading
    this.getCityControl()?.disable();
    this.getZipcodeControl()?.disable();

    forkJoin({
      counties: this.addressService.getCounties(state.id),
      cities: this.addressService.getCities(state.id),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ counties, cities }) => {
        this.counties = counties;
        this.availableCounties = counties.map((c) => c.name);

        this.cities = cities;
        this.availableCities = cities.map((c) => c.name);

        // Enable city control when cities are loaded
        this.getCityControl()?.enable();
      });

    this.availableZipcodes = [];
  }

  onCountyChange(property: Properties, event: Event): void {
    this.onPropertyUpdate(property.property_name, event);
    const state = this.states.find(
      (s) => s.name === this.getCurrentStateValue(),
    );
    if (!state) return;
    of(this.cities)
      .pipe(
        switchMap((cities) => {
          // If cities already loaded, reuse them
          if (cities.length > 0) {
            return of(cities);
          }

          // Otherwise fetch from API
          return this.addressService.getCities(state.id);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((cities) => {
        this.cities = cities;
        this.availableCities = cities.map((c) => c.name);
        // Enable city control when cities are loaded
        this.getCityControl()?.enable();
      });
  }

  onCityChange(event: Event) {
    const cityName = (event.target as HTMLSelectElement).value;

    const city = this.cities.find((c) => c.name === cityName);

    if (!city) {
      this.availableZipcodes = [];
      // Disable zipcode control when no city selected
      this.getZipcodeControl()?.disable();
      return;
    }

    // Disable zipcode control while loading
    this.getZipcodeControl()?.disable();

    this.addressService
      .getZipcodes(city.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((zips) => {
        this.zipcodes = zips;
        this.availableZipcodes = zips.map((z) => z.code);
        const zipProperty = this.recordDetails?.detail.properties.find(
          (p) => p.property.toLowerCase() === 'zip_code',
        );
        const zipControl = zipProperty
          ? this.propertyForm.get(zipProperty.property_name)
          : null;

        if (zipControl) {
          zipControl.setValue('');
          zipControl.markAsPristine();
          // Enable zipcode control when zipcodes are loaded
          zipControl.enable();
        }
      });
  }

  isCityProperty(property: Properties): boolean {
    return property.property_name.toLowerCase() === 'city';
  }

  isZipcodeProperty(property: Properties): boolean {
    const name = property.property_name.toLowerCase().replace(/\s+/g, '');

    return name === 'zipcode';
  }

  getCurrentStateValue(): string {
    // Get current state value from form or record details
    const stateProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property_name.toLowerCase() === 'state',
    );
    if (stateProperty) {
      return (
        this.propertyForm.get(stateProperty.property_name)?.value ||
        stateProperty.value ||
        ''
      );
    }
    return this.selectedStateForCounty;
  }

  // Helper method to get the City form control
  private getCityControl() {
    const cityProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property.toLowerCase() === 'city',
    );
    return cityProperty
      ? this.propertyForm.get(cityProperty.property_name)
      : null;
  }

  // Helper method to get the Zipcode form control
  private getZipcodeControl() {
    const zipProperty = this.recordDetails?.detail.properties.find(
      (p) => p.property.toLowerCase() === 'zip_code',
    );
    return zipProperty
      ? this.propertyForm.get(zipProperty.property_name)
      : null;
  }

  isMultiselectValueSelected(property: Properties, label: string): boolean {
    const value = this.propertyForm.get(property.property_name)?.value;
    if (!Array.isArray(value)) {
      return false;
    }

    // Get the option value for this label and check if it's in the array
    const optionValue = this.getOptionValue(property.options, label);
    return value.includes(optionValue);
  }

  onMultiselectChange(property: Properties, label: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const currentValue =
      this.propertyForm.get(property.property_name)?.value || [];

    // Get the option value for this label
    const optionValue = this.getOptionValue(property.options, label);

    let newValue: (string | number | boolean)[];

    if (target.checked) {
      // Add option value if checked
      newValue = [...currentValue, optionValue];
    } else {
      // Remove option value if unchecked
      newValue = currentValue.filter(
        (v: string | number | boolean) => v !== optionValue,
      );
    }

    this.propertyForm.patchValue(
      {
        [property.property_name]: newValue,
      },
      { emitEvent: false },
    );

    this.updatedProperties[property.property_name] = newValue;
    this.isDirty = true;

    // If Interested Products changed, update allowed groups
    if (this.isInterestedProductsProperty(property) && this.recordDetails) {
      // Convert option values back to labels for group filtering
      const selectedLabels = newValue
        .map((val) => this.getOptionLabel(property.options, val))
        .filter((label) => label !== null) as string[];

      // Create a temporary CompanyDetails object with updated value (as labels for filtering)
      const tempDetails: CompanyDetails = {
        ...this.recordDetails,
        detail: {
          ...this.recordDetails.detail,
          properties: this.recordDetails.detail.properties.map((p) =>
            this.isInterestedProductsProperty(p)
              ? ({ ...p, value: selectedLabels } as unknown as Properties)
              : p,
          ),
        },
      };
      this.initializeAllowedGroups(tempDetails);
    }
  }

  private findInterestedProductsProperty(
    properties: Properties[],
  ): Properties | undefined {
    return properties.find((property) =>
      this.isInterestedProductsProperty(property),
    );
  }

  private isInterestedProductsProperty(property: Properties): boolean {
    return (
      property.property_name === 'Interested Products' ||
      property.property === 'interested_products'
    );
  }

  private closeRecordPopup(): void {
    this.popupClosed.emit();
    if (this.vlfUnassigned) {
      this.vlfUnassigned = false;
      this.triggerSearchResults.emit(true);
    }
  }

  closePopup(isClickedOutside: boolean): void {
    if (!isClickedOutside) {
      if (this.isDirty) {
        this.showUnsavedChangesPopup = true;
      } else {
        this.closeRecordPopup();
      }
    }
  }

  onShowUsersDropDown(): void {
    this.userSuggestions = [];
    this.selectedConnections = {};
    this.selectedPeopleToShare = [];
    this.selectedUsersWithNames = [];
    this.selectedThroughConnections.clear();
    this.showUsersDropdown = true;
    this.getAllConnections();
  }

  // Opens the visibility settings modal
  openVisibilityModal(): void {
    this.showVisibilityModal = true;
  }

  closeVisibilityModal(): void {
    this.showVisibilityModal = false;
    this.selectedConnection = null;
    this.availableUsers = [];

    this.getAllConnections();
  }

  getAllConnections(isLoadMore = false): void {
    if (!isLoadMore && this.availableConnections.length > 0) {
      return;
    }
    if (isLoadMore) {
      this.connectionCurrentPage++;
    } else {
      this.connectionCurrentPage = 1;
      this.availableConnections = [];
    }

    this.isLoadingConnections = true;
    const searchTerm =
      (document.querySelector('.search-users input') as HTMLInputElement)
        ?.value || '';

    this.searchService
      .getRecordConnections(
        this.company.unique_id,
        searchTerm,
        this.connectionCurrentPage,
        this.connectionPageSize,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (isLoadMore) {
            this.availableConnections = [
              ...this.availableConnections,
              ...res.results,
            ];
          } else {
            this.availableConnections = res.results;
          }

          // Manual pagination logic: check if we received a full page
          this.hasMoreConnections =
            this.availableConnections.length < res.count;
          this.isLoadingConnections = false;
          this.isLoadingMoreConnections = false;
        },
        error: (err) => {
          console.error('Error fetching connections:', err);
          this.isLoadingConnections = false;
          this.isLoadingMoreConnections = false;
        },
      });
  }

  loadMoreConnections(): void {
    if (this.isLoadingMoreConnections || !this.hasMoreConnections) return;
    this.isLoadingMoreConnections = true;
    this.getAllConnections(true);
  }

  onSelectConnection(connection: SharedConnectionUsers): void {
    if (this.selectedConnection?.id === connection.id) {
      this.selectedConnection = null; // Collapse if already selected
      this.availableUsers = [];
      return;
    }

    this.selectedConnection = connection;
    this.userSearchKey = ''; // Reset search on new connection selection
    this.userCurrentPage = 1;
    this.availableUsers = [];
    this.loadUsersForConnection(connection.id);
  }

  loadUsersForConnection(connectionId: number, isLoadMore = false): void {
    if (isLoadMore) {
      this.userCurrentPage++;
    } else {
      this.userCurrentPage = 1;
      this.availableUsers = [];
    }

    this.isLoadingConnectionUsers = true;

    this.searchService
      .getRecordConnectionUsers(
        this.company.unique_id,
        connectionId,
        this.userCurrentPage,
        this.userPageSize,
        this.userSearchKey,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const connectionResult = res.results.find(
            (c: SharedConnectionUsers) => c.id === connectionId,
          );
          const newUsers: ExtendedConnectionUser[] = connectionResult
            ? connectionResult.users.map((u: ConnectionUser) => ({
                ...u,
                name: `${u.first_name} ${u.last_name}`,
              }))
            : [];

          if (isLoadMore) {
            this.availableUsers = [...this.availableUsers, ...newUsers];
          } else {
            this.availableUsers = newUsers;
          }

          // Check if the connection is already selected, if so, mark these users as selected
          const connection = this.availableConnections.find(
            (c) => c.id === connectionId,
          );
          if (
            connection &&
            this.selectedConnections[connection.connection_name]
          ) {
            newUsers.forEach((user: ExtendedConnectionUser) => {
              this.selectedThroughConnections.add(user.username);
              // Remove from individual selection if present to avoid duplication/inconsistency
              if (this.selectedPeopleToShare.includes(user.username)) {
                this.selectedPeopleToShare = this.selectedPeopleToShare.filter(
                  (u) => u !== user.username,
                );
                this.selectedUsersWithNames =
                  this.selectedUsersWithNames.filter(
                    (u) => u.username !== user.username,
                  );
              }
            });
          }

          this.hasMoreUsers = this.availableUsers.length < res.count;
          this.isLoadingConnectionUsers = false;
        },
        error: (err) => {
          console.error('Error fetching users:', err);
          this.isLoadingConnectionUsers = false;
        },
      });
  }

  loadMoreUsers(): void {
    if (
      this.isLoadingConnectionUsers ||
      !this.hasMoreUsers ||
      !this.selectedConnection
    )
      return;
    this.loadUsersForConnection(this.selectedConnection.id, true);
  }

  onSearchUser(): void {
    // When searching, we search connections first
    this.getAllConnections();
  }

  onSearchConnectionUser(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.userSearchSubject.next(target.value);
  }

  openInvitePopup(): void {
    this.showInvitePopup = true;
  }

  closeInvitePopup(): void {
    this.showInvitePopup = false;
  }

  openArcgisPopup(preloadedData?: Record<string, unknown>): void {
    if (!this.company?.latitude || !this.company?.longitude) {
      this.commonService.triggerToast({
        type: ToastType.Error,
        message: 'Location coordinates not available for this record',
      });
      return;
    }
    this.preloadedArcgisData = preloadedData;
    this.showArcgisPopup = true;
  }

  closeArcgisPopup(): void {
    this.showArcgisPopup = false;
    this.preloadedArcgisData = undefined; // Clear data when closing
  }

  onUsersSelection(
    event: Event,
    assignee: string | undefined, // assignee is username for user, connection_name for connection
    isConnection = false,
    userObj?: { username: string; name: string },
  ): void {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;

    if (isConnection && assignee) {
      if (isChecked) {
        const connection = this.availableConnections.find(
          (c) => c.connection_name === assignee,
        );
        if (connection) {
          this.selectedConnections[assignee] = connection;
        }
      } else {
        delete this.selectedConnections[assignee];
      }

      const connection = this.availableConnections.find(
        (c) => c.connection_name === assignee,
      );

      if (connection) {
        // If we have users loaded for this connection (i.e. it is expanded), we should update their state
        if (this.selectedConnection?.id === connection.id) {
          this.availableUsers.forEach((user) => {
            if (isChecked) {
              this.selectedThroughConnections.add(user.username);
              // If user was individually selected, remove from individual list as it's now covered by connection
              if (this.selectedPeopleToShare.includes(user.username)) {
                this.selectedPeopleToShare = this.selectedPeopleToShare.filter(
                  (u) => u !== user.username,
                );
                this.selectedUsersWithNames =
                  this.selectedUsersWithNames.filter(
                    (u) => u.username !== user.username,
                  );
              }
            } else {
              this.selectedThroughConnections.delete(user.username);
            }
          });
        }
      }
    } else if (userObj) {
      // Individual user selection
      if (!this.selectedThroughConnections.has(userObj.username)) {
        if (isChecked) {
          if (!this.selectedPeopleToShare.includes(userObj.username)) {
            this.selectedPeopleToShare.push(userObj.username);
            this.selectedUsersWithNames.push({
              username: userObj.username,
              displayName: userObj.name,
            });
          }
        } else {
          this.selectedPeopleToShare = this.selectedPeopleToShare.filter(
            (u) => u !== userObj.username,
          );
          this.selectedUsersWithNames = this.selectedUsersWithNames.filter(
            (u) => u.username !== userObj.username,
          );
        }
      }
    }
  }

  getSelectedUsers(): string[] {
    // Get users that weren't selected through connections
    return this.selectedUsersWithNames
      .filter((user) => !this.selectedThroughConnections.has(user.username))
      .map((user) => user.displayName);
  }

  removeUser(displayName: string): void {
    const user = this.selectedUsersWithNames.find(
      (u) => u.displayName === displayName,
    );
    if (user) {
      this.selectedPeopleToShare = this.selectedPeopleToShare.filter(
        (username) => username !== user.username,
      );
      this.selectedUsersWithNames = this.selectedUsersWithNames.filter(
        (u) => u.displayName !== displayName,
      );
    }
  }

  sendOutreachEmail(): void {
    if (!this.selectedOutreachFormType) {
      return; // Don't proceed if no form type is selected
    }

    this.showSendOutreachEmailConfirm = false;
    this.outreachService
      .sendEmail(this.company.unique_id, this.selectedOutreachFormType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const formTypeItem = this.outreachFormTypes.find(
            (type) => type.value === this.selectedOutreachFormType,
          );
          const formLabel = formTypeItem
            ? formTypeItem.label
            : this.selectedOutreachFormType;
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: `${formLabel} sent successfully!`,
          });

          this.selectedOutreachFormType = null; // Reset selection
        },
        error: (err) => {
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: 'Failed to send email! ' + err.error.detail,
          });
          this.selectedOutreachFormType = null; // Reset selection
        },
      });
  }

  cancelOutreachEmail(): void {
    this.showSendOutreachEmailConfirm = false;
    this.selectedOutreachFormType = null;
  }

  isOutreachFormValid(): boolean {
    return this.selectedOutreachFormType !== null;
  }

  shareWithSelectedPeople(): void {
    const selectedConnections = Object.keys(this.selectedConnections);

    // Filter out users that are selected through connections
    const selectedUsers = this.selectedPeopleToShare.filter(
      (user) => !this.selectedThroughConnections.has(user),
    );

    this.auditLogService.logFeature({
      featureName: 'share_lead_info',
      action: 'share',
      meta: {
        recordId: this.company.unique_id,
        recordName: this.company.company_name,
        connectionsCount: String(selectedConnections.length),
        usersCount: String(selectedUsers.length),
      },
      description: 'User shares lead information',
    });

    const payload: ShareRecord = {
      connections: selectedConnections,
      users: selectedUsers,
    };

    this.searchService
      .shareRecord(payload, this.company.unique_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showUsersDropdown = false;
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Record shared successfully!',
          });
        },
        error: () => {
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: 'Failed to share record!',
          });
        },
      });
  }

  onCloseUsersDropDown(): void {
    this.showUsersDropdown = false;
    this.selectedConnections = {};
    this.selectedThroughConnections.clear();
    this.selectedPeopleToShare = [];
    this.selectedUsersWithNames = [];
    this.userSearchKey = '';
    this.selectedConnection = null;
    this.availableUsers = [];
  }

  onAssignButtonHover(isHovered: boolean): void {
    // Only set hover state if button is disabled due to quota being exceeded
    if (this.isQuotaExceeded()) {
      this.isAssignButtonHovered = isHovered;
    }
  }

  isQuotaExceeded(): boolean {
    return !!(
      this.recordDetails?.lead_quota &&
      this.recordDetails.lead_quota.lead_count >=
        this.recordDetails.lead_quota.quota
    );
  }

  getSelectedConnections(): {
    connectionName: string;
    displayName: string;
  }[] {
    return Object.values(this.selectedConnections).map((connection) => ({
      connectionName: connection.connection_name,
      displayName: connection.display_name || connection.connection_name,
    }));
  }

  removeConnection(connectionName: string): void {
    delete this.selectedConnections[connectionName];
    const connectionUsers =
      this.userSuggestions
        .find((s) => s.connection_name === connectionName)
        ?.users.map((u) => u.username) || [];

    connectionUsers.forEach((user) => {
      this.selectedThroughConnections.delete(user);
      this.selectedPeopleToShare = this.selectedPeopleToShare.filter(
        (p) => p !== user,
      );
      this.selectedUsersWithNames = this.selectedUsersWithNames.filter(
        (u) => u.username !== user,
      );
    });
  }

  confirmClose(): void {
    this.showUnsavedChangesPopup = false;
    this.isDirty = false;
    this.updatedProperties = {};
    this.closeRecordPopup();
  }

  cancelClose(): void {
    this.showUnsavedChangesPopup = false;
  }

  isAllowedForConnection(): boolean {
    if (
      location.hostname === 'www.accdata.org' ||
      location.hostname === 'accdata.org'
    ) {
      const loggedInUserInfo = this.authService.getLoggedInUserInfo();
      if (loggedInUserInfo?.connection === 'cci-north-carolina-auth0')
        return true;
      return false;
    }

    return true;
  }

  toggleAssignedStatus(): void {
    if (this.recordDetails!.assigned_to) {
      this.showUnassignPopup = true;
    } else if (this.recordDetails!.source_type === 'VLF') {
      // VLF leads: show confirmation popup before assigning (first-come-first-served)
      this.showVlfClaimModal = true;
    } else {
      this.assignLead();
    }
  }

  downloadPDF() {
    if (this.pdfContent && !this.isDownloadingPdf) {
      this.auditLogService.logFeature({
        featureName: 'export_lead_info_pdf',
        action: 'download',
        meta: {
          recordId: this.company.unique_id,
          recordName: this.company.company_name,
        },
        description: 'User downloads lead info as PDF',
      });

      this.isDownloadingPdf = true;
      this.pdfDownloadService
        .generateAndDownloadPDF(this.pdfContent.nativeElement)
        .finally(() => {
          this.isDownloadingPdf = false;
        });
    }
  }

  assignLead(): void {
    this.auditLogService.logFeature({
      featureName: 'lead',
      action: 'assign_to_self',
      meta: {
        recordId: this.company.unique_id,
        recordName: this.company.company_name,
      },
      description: 'User assigns a lead to themselves',
    });

    this.leadsService
      .assignLead([this.company])
      .pipe(
        takeUntil(this.destroy$),
        switchMap((res: AssignLeadResponse) => {
          this.crmStatus = res.crm_status;

          const successData = res.success?.[0];
          if (!successData) {
            const failedReason =
              res.failure?.[0]?.errorMessage || 'Lead assignment failed';
            this.commonService.triggerToast({
              type: ToastType.Error,
              message: failedReason,
            });
            return throwError(() => new Error(failedReason));
          }
          this.company.leadId = successData.id;
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Lead successfully assigned to you!',
          });

          this.showLeadsHistoryInline = false;

          // Update assigned_to
          this.recordDetails!.assigned_to = this.getAssignedToInfo();
          this.company.assigned_to = this.recordDetails!.assigned_to;

          // Update quota count immediately
          if (this.recordDetails!.lead_quota) {
            this.recordDetails!.lead_quota.lead_count += 1;
          }

          // Update assignees arrays - add current user if not already present
          const currentUserConnection = {
            connection_name: this.loggedInUserInfo?.connection ?? '',
            id: 0,
            display_name: this.loggedInUserInfo?.connection_display_name ?? '',
            assigned_on_utc: new Date(),
          };

          const existsInRecordDetails = this.recordDetails!.assignees?.some(
            (assignee) =>
              assignee.connection_name === this.loggedInUserInfo?.connection,
          );
          const existsInCompany = this.company.assignees?.some(
            (assignee) =>
              assignee.connection_name === this.loggedInUserInfo?.connection,
          );

          if (!existsInRecordDetails) {
            if (!this.recordDetails!.assignees) {
              this.recordDetails!.assignees = [];
            }
            this.recordDetails!.assignees.push(currentUserConnection);
          }

          if (!existsInCompany) {
            if (!this.company.assignees) {
              this.company.assignees = [];
            }
            this.company.assignees.push(currentUserConnection);
          }

          this.checkEditDelete();
          this.assignedLead.emit(this.company);
          // Reset CRM lead ID when a new lead is assigned
          this.crmLeadId = null;

          return this.loadLeadAttributes(successData.id);
        }),
      )
      .subscribe({
        next: () => {
          this.refreshInfo(false);
        },
        error: (err) => {
          console.log(err);
        },
      });
  }

  getAssignedToInfo(): Assignee {
    const assignedUserInfo = new Assignee();
    assignedUserInfo.username = this.loggedInUserInfo?.username;
    assignedUserInfo.first_name = this.loggedInUserInfo?.name?.split(' ')[0];
    assignedUserInfo.group_name = this.loggedInUserInfo?.user_group;
    return assignedUserInfo;
  }

  getCurrentLeadStatus(): string | number | null {
    const isRealStatus = (val: unknown): boolean => {
      if (val === null || val === undefined || val === '') return false;
      const str = String(val).trim().toLowerCase();
      return ![
        '0',
        'null',
        'undefined',
        'not defined',
        'not_defined',
        'not set',
        'not_set',
        'select lead status',
      ].includes(str);
    };

    const userConnection = this.loggedInUserInfo?.connection?.toLowerCase();
    const userUsername = this.loggedInUserInfo?.username?.toLowerCase();
    const isCurrentUser = (
      a:
        | { connection_name?: string | null; username?: string | null }
        | null
        | undefined,
    ) =>
      Boolean(
        (userConnection &&
          a?.connection_name?.toLowerCase() === userConnection) ||
          (userUsername &&
            a?.connection_name?.toLowerCase() === userUsername) ||
          (userUsername && a?.username?.toLowerCase() === userUsername),
      );

    const currentUserAssignee =
      this.recordDetails?.assignees?.find(isCurrentUser) ||
      this.company?.assignees?.find(isCurrentUser);

    const statusProp = this.recordDetails?.detail?.properties?.find(
      (p) =>
        p.property === 'lead_info::lead_status' ||
        p.property_name?.toLowerCase() === 'lead status',
    );
    const formPropVal = statusProp
      ? (this.propertyForm?.get(statusProp.property_name)?.value ??
        statusProp.value)
      : null;

    // Ordered candidate sources:
    const candidates = [
      this.leadInfoComponent?.leadAttributes?.['lead_status'],
      this.company?.lead_status,
      (this.recordDetails as { lead_status?: string | number })?.lead_status,
      currentUserAssignee?.lead_status,
      this.recordDetails?.lead_attributes?.['lead_status'],
      this.company?.lead_attributes?.['lead_status'],
      formPropVal,
      this.recordDetails?.assignees?.[0]?.lead_status,
      this.company?.assignees?.[0]?.lead_status,
    ];

    // 1. Return the first valid, active status
    for (const val of candidates) {
      if (isRealStatus(val)) {
        return val as string | number;
      }
    }

    // 2. Fallback: return the first non-null/undefined value if any exists
    for (const val of candidates) {
      if (val !== undefined && val !== null && val !== '') {
        return val as string | number;
      }
    }

    return null;
  }

  OnConfirmUnassign(event?: {
    selectedStatus?: string;
    statusNotes?: string;
  }): void {
    const newLeadStatus = event?.selectedStatus;
    const newStatusNotes = event?.statusNotes;
    this.isUnassigning = true;

    this.auditLogService.logFeature({
      featureName: 'lead',
      action: 'unassign',
      meta: {
        recordId: this.company.unique_id,
        recordName: this.company.company_name,
      },
      description: 'User unassigns a lead',
    });

    if (newStatusNotes !== undefined) {
      if (this.recordDetails?.lead_attributes) {
        this.recordDetails.lead_attributes['status_notes'] = newStatusNotes;
      }
      if (this.company?.lead_attributes) {
        this.company.lead_attributes['status_notes'] = newStatusNotes;
      }
    }

    const statusUpdate$ = newLeadStatus
      ? this.leadsService
          .updateLeadStatus([
            {
              unique_id: this.company.unique_id,
              lead_status: newLeadStatus,
            },
          ])
          .pipe(
            tap(() => {
              this.leadsService.triggerStatusCountRefresh();
              this.leadStatusUpdated.emit({
                uniqueId: this.company.unique_id,
                newStatus: newLeadStatus,
              });
              if (this.recordDetails?.lead_attributes) {
                this.recordDetails.lead_attributes['lead_status'] =
                  Number(newLeadStatus) || newLeadStatus;
              }
              if (this.company?.lead_attributes) {
                this.company.lead_attributes['lead_status'] =
                  Number(newLeadStatus) || newLeadStatus;
              }
            }),
          )
      : of(null);

    const unassign$ = this.leadsService.unassignLead(this.company.unique_id);

    statusUpdate$
      .pipe(
        switchMap(() => unassign$),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.isUnassigning = false;
          this.showLeadsHistoryInline = false;
          this.showUnassignPopup = false;
          this.recordDetails!.assigned_to = null;
          this.company.assigned_to = null;

          // Update quota count immediately
          if (this.recordDetails!.lead_quota) {
            this.recordDetails!.lead_quota.lead_count = Math.max(
              0,
              this.recordDetails!.lead_quota.lead_count - 1,
            );
          }

          // Remove current user from assignees arrays
          if (this.recordDetails!.assignees) {
            this.recordDetails!.assignees =
              this.recordDetails!.assignees.filter(
                (assignee) =>
                  assignee.connection_name !==
                  this.loggedInUserInfo?.connection,
              );
          }
          if (this.company.assignees) {
            this.company.assignees = this.company.assignees.filter(
              (assignee) =>
                assignee.connection_name !== this.loggedInUserInfo?.connection,
            );
          }

          this.showScore = false;
          this.checkEditDelete();
          this.assignedLead.emit(this.company);

          // Reset CRM lead ID when lead is unassigned
          this.crmLeadId = null;

          if (this.company.source === 'VLF') {
            this.vlfUnassigned = true;
            return;
          }

          this.refreshInfo(false);
        },
        error: (err) => {
          this.isUnassigning = false;
          console.error(err);
        },
      });
  }

  refreshInfo(clearCache: boolean): void {
    const searchDetailFilter: SearchDetailFilter = {
      unique_id: this.company.unique_id,
      category: '101',
      nocache: clearCache ? 'true' : 'false',
      source: this.company.source,
    };
    this.loadCompanyDetails(searchDetailFilter);
  }

  onVisibilityUpdated(): void {
    this.refreshInfo(false);
    this.commonService.triggerToast({
      type: ToastType.Success,
      message: 'Visibility updated successfully!',
    });
  }

  confirmDelete(): void {
    this.auditLogService.logFeature({
      featureName: 'delete_lead_info',
      action: 'delete',
      meta: {
        recordId: this.company.unique_id,
        recordName: this.company.company_name,
      },
      description: 'User deletes a business record',
    });

    this.searchService
      .deleteCompanyDetails(this.company.unique_id, this.company.source)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Record deleted successfully!',
          });
          this.showDeletePopup = false;
          setTimeout(() => {
            this.companyDeleted.emit(this.company.unique_id);
            this.closeRecordPopup();
          }, 1000);
        },
        error: () => {
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: 'Failed to delete record!',
          });
          this.showDeletePopup = false;
        },
      });
  }

  cancelDelete(): void {
    this.showDeletePopup = false;
  }

  toggleApplicationSummary(): void {
    this.applicationSummaryExpanded = !this.applicationSummaryExpanded;
  }

  onApplicationSummaryChange(value: string): void {
    this.applicationSummary = value;

    this.propertyForm.get('Application Summary')?.setValue(value);
    this.propertyForm.get('Application Summary')?.markAsDirty();
    this.propertyForm.get('Application Summary')?.updateValueAndValidity();

    const applicationSummaryProperty =
      this.recordDetails?.detail.properties.find(
        (prop) => prop.property === 'application_summary',
      );
    if (applicationSummaryProperty) {
      applicationSummaryProperty.value = value;
    }
    this.isDirty = true;
    this.updatedProperties['application_summary'] = value;
  }

  getGroupedProperties(): GroupedProperties[] {
    if (!this.recordDetails?.detail?.properties) {
      return [];
    }

    this.applicationSummary = null;

    const groupMap = new Map<string | null, Properties[]>();

    this.recordDetails.detail.properties.forEach((property) => {
      const group = property.group || null;
      // Skip properties in the Propensity Details group as they'll be shown in the score section
      if (group === 'Propensity Details') {
        return;
      }
      if (
        group === 'Basic Information' &&
        property.property === 'application_summary'
      ) {
        this.applicationSummary = property.value;
        return;
      }

      if (!groupMap.has(group)) {
        groupMap.set(group, []);
      }
      groupMap.get(group)?.push(property);
    });

    return Array.from(groupMap.entries()).map(([group, properties]) => ({
      group,
      properties,
    }));
  }

  toggleTextExpansion(companyId: string, propertyKey: string): void {
    if (!this.additionalDetailsExpanded[companyId]) {
      this.additionalDetailsExpanded[companyId] = {};
    }
    this.additionalDetailsExpanded[companyId][propertyKey] =
      !this.additionalDetailsExpanded[companyId][propertyKey];
  }

  isExpandableText(property: Properties): boolean {
    return !!property.value && property.value.length > 100;
  }

  isExpandableList(property: Properties): boolean {
    return (
      (property.type === 'list' || property.type === 'multiselect') &&
      Array.isArray(property.value) &&
      property.value.length > 4
    ); // Show more/less if more than 4 items
  }

  // Get display value for a property (converts values to labels for dropdown/radio/multiselect)
  getDisplayValue(property: Properties): string {
    // Check for null, undefined, or empty string
    if (
      property.value === null ||
      property.value === undefined ||
      property.value === ''
    ) {
      return '-';
    }

    // Special handling for business_industry to map value to label
    if (property.property === 'business_industry') {
      const industry = INDUSTRY_OPTIONS.find(
        (opt) => opt.value === String(property.value),
      );
      return industry ? industry.label : String(property.value);
    }

    // For dropdown and radio, convert value to label
    if (
      (property.type === 'dropdown' || property.type === 'radio') &&
      property.options
    ) {
      // Try to convert numeric value to label
      const label = this.getOptionLabel(property.options, property.value);
      // If label found, use it; otherwise value might already be a label, so just return it
      return label || String(property.value);
    }

    // For multiselect, convert array of values to array of labels
    if (
      property.type === 'multiselect' &&
      Array.isArray(property.value) &&
      property.options
    ) {
      return property.value
        .map((val) => this.getOptionLabel(property.options, val) || String(val))
        .join(', ');
    }

    // For other types, return the value as is
    return String(property.value);
  }

  // Get display values as array for multiselect (convert option values to labels)
  getMultiselectDisplayValues(property: Properties): string[] {
    if (!Array.isArray(property.value)) {
      return [];
    }
    // Convert option values to labels for display
    return property.value
      .map((val) => this.getOptionLabel(property.options, val) || String(val))
      .filter((label) => label !== null) as string[];
  }

  formatLongText(value: string): string {
    if (!value) return '';
    // If value contains lines starting with 1. 2. 3. etc, format as <ol><li>...</li></ol>
    const lines = value.split(/\r?\n/).filter((line) => line.trim() !== '');
    const isNumberedList = lines.every((line) => /^\d+\./.test(line.trim()));
    if (isNumberedList) {
      return (
        '<ol>' +
        lines
          .map((line) => '<li>' + line.replace(/^\d+\.\s*/, '') + '</li>')
          .join('') +
        '</ol>'
      );
    }
    return value.replace(/\n/g, '<br>');
  }
  // Table helper methods
  getTableHeaders(tableData: unknown): string[] {
    if (!tableData || !Array.isArray(tableData) || tableData.length === 0)
      return [];
    const firstRow = tableData[0];
    if (typeof firstRow === 'object' && firstRow !== null) {
      return Object.keys(firstRow);
    }
    return [];
  }

  formatHeaderName(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  getTableCellValue(row: unknown, key: string): string {
    if (typeof row !== 'object' || row === null) return '-';
    const value = (row as Record<string, unknown>)[key];
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') {
      // Format percentages if key contains 'percentage'
      if (key.toLowerCase().includes('percentage')) {
        return `${value}%`;
      }
      return value.toString();
    }
    return String(value);
  }

  // Get raw table cell value without formatting (for input binding)
  getRawTableCellValue(row: unknown, key: string): string | number {
    if (typeof row !== 'object' || row === null) return '';
    const value = (row as Record<string, unknown>)[key];
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return value;
    return String(value);
  }

  getTableData(value: unknown): unknown[] {
    if (Array.isArray(value)) {
      return value;
    }
    return [];
  }

  // Initialize table edit data when entering edit mode
  initializeTableEditData(propertyName: string, value: unknown): void {
    if (!this.tableEditData[propertyName]) {
      if (Array.isArray(value) && value.length > 0) {
        // Deep clone the table data
        this.tableEditData[propertyName] = JSON.parse(JSON.stringify(value));
      } else {
        // Initialize with one empty row if no data exists
        this.tableEditData[propertyName] = [this.createEmptyTableRow(value)];
      }
    }
  }

  createEmptyTableRow(existingData: unknown): Record<string, unknown> {
    // Get headers from existing data or use defaults for ownership structure
    let headers: string[];
    if (Array.isArray(existingData) && existingData.length > 0) {
      headers = Object.keys(existingData[0] as Record<string, unknown>);
    } else {
      // Default headers for ownership structure
      headers = [
        'owner_name',
        'ownership_percentage',
        'other_business_percentage',
      ];
    }

    const emptyRow: Record<string, unknown> = {};
    headers.forEach((header) => {
      if (header.includes('percentage')) {
        emptyRow[header] = 0;
      } else {
        emptyRow[header] = '';
      }
    });
    return emptyRow;
  }

  getEditableTableData(
    propertyName: string,
    originalValue: unknown,
  ): unknown[] {
    if (!this.tableEditData[propertyName]) {
      this.initializeTableEditData(propertyName, originalValue);
    }
    return this.tableEditData[propertyName] || [];
  }

  addTableRow(propertyName: string, originalValue: unknown): void {
    if (!this.tableEditData[propertyName]) {
      this.initializeTableEditData(propertyName, originalValue);
    }
    this.tableEditData[propertyName].push(
      this.createEmptyTableRow(originalValue),
    );
    this.updatedProperties[propertyName] = this.tableEditData[
      propertyName
    ] as unknown;
    this.isDirty = true;
  }

  removeTableRow(propertyName: string, index: number): void {
    if (
      this.tableEditData[propertyName] &&
      this.tableEditData[propertyName].length > index
    ) {
      this.tableEditData[propertyName].splice(index, 1);
      // Keep at least one row
      if (this.tableEditData[propertyName].length === 0) {
        this.tableEditData[propertyName] = [
          this.createEmptyTableRow(this.tableEditData[propertyName]),
        ];
      }
      this.updatedProperties[propertyName] = this.tableEditData[
        propertyName
      ] as unknown;
      this.isDirty = true;
    }
  }

  updateTableCell(
    propertyName: string,
    rowIndex: number,
    cellKey: string,
    value: string,
  ): void {
    if (!this.tableEditData[propertyName]) {
      return;
    }

    const row = this.tableEditData[propertyName][rowIndex] as Record<
      string,
      unknown
    >;
    if (row) {
      // Convert to number if it's a percentage field
      if (cellKey.includes('percentage')) {
        row[cellKey] = value === '' ? 0 : Number(value);
      } else {
        row[cellKey] = value;
      }
      this.updatedProperties[propertyName] = this.tableEditData[
        propertyName
      ] as unknown;
      this.isDirty = true;
    }
  }

  toggleLeadsHistory(): void {
    if (!this.showLeadsHistoryInline && this.recordDetails?.unique_id) {
      this.handleLoadLeadsHistory(this.recordDetails.unique_id);
    }
    this.showLeadsHistoryInline = !this.showLeadsHistoryInline;
  }

  isLoanFormFilled(includeCustom = true): boolean {
    const properties = this.recordDetails?.detail?.properties ?? [];

    return (
      (properties.some(
        (p) =>
          ((p.property === 'form_data_generic::loan_type' ||
            p.property === 'form_data_generic_s2::loan_type') &&
            p.value === 'Term Loans') ||
          p.property.startsWith('form_data_loan::') ||
          (p.property === 'form_data_generic_v2::will_fill_prescreening' &&
            p.value === 'Yes'),
      ) ||
        properties.some(
          (p) =>
            includeCustom &&
            p.property === 'loan_type' &&
            p.value === 'Term Loans',
        ) ||
        (properties.some(
          (p) =>
            includeCustom &&
            p.property === 'capital_product_type' &&
            Array.isArray(p.value) &&
            !(p.value.length == 1 && p.value[0] === 'Grant Programs'),
        ) &&
          properties.some(
            (p) =>
              includeCustom &&
              p.property === 'will_fill_prescreening' &&
              p.value === 'Yes',
          ))) &&
      this.hasValueForAllScoringProperties()
    );
  }

  hasValueForAllScoringProperties(): boolean {
    const propKeysToCheck = [
      'period_months',
      'total_revenue',
      'number_of_employees',
      'business_established',
    ];
    const properties = this.recordDetails?.detail?.properties ?? [];
    return propKeysToCheck.every((key) =>
      properties.some(
        (p) =>
          p.property.endsWith(key) &&
          p.value !== null &&
          p.value !== undefined &&
          p.value !== '',
      ),
    );
  }

  openCRMRecord(): void {
    if (!this.recordDetails?.unique_id || this.isLoadingCRM) {
      return;
    }

    this.isLoadingCRM = true;
    this.crmError = null;

    const crmToken = localStorage.getItem('crm_token');
    const crmTokenExpiresOn = localStorage.getItem('crm_token_expires_on_utc');

    // Check if token exists and is not expired
    const isTokenValid =
      crmToken && crmTokenExpiresOn && new Date(crmTokenExpiresOn) > new Date();

    if (!this.crmLeadId) {
      // If crmLeadId is not already loaded, fetch it with retries
      this.crmService
        .getCRMLeadID(this.recordDetails.unique_id)
        .pipe(
          retry({
            count: 20, // Maximum 20 retry attempts
            delay: 500, // Wait 500ms between retries
            resetOnSuccess: true, // Reset retry count on success (optional)
          }),
          takeUntil(this.destroy$),
        )
        .subscribe({
          next: (leadId) => {
            this.crmLeadId = leadId;
            this.crmStatus = true;
            if (isTokenValid) {
              this.navigateToCRM(this.crmLeadId, crmToken!);
            } else {
              this.getCRMToken();
            }
          },
          error: (error) => {
            console.error('Failed to fetch CRM Lead ID after retries:', error);
            this.isLoadingCRM = false;
            this.crmError = 'Failed to fetch CRM record. Please try again.';
          },
        });
    } else if (isTokenValid) {
      this.navigateToCRM(this.crmLeadId, crmToken!);
    } else {
      this.getCRMToken();
    }
  }

  private getCRMToken(): void {
    this.authService
      .getCRMAuthToken()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (crmRecord) => {
          localStorage.setItem('crm_token', crmRecord.access!);
          localStorage.setItem(
            'crm_token_expires_on_utc',
            crmRecord.token_expiry!,
          );
          this.navigateToCRM(this.crmLeadId!, crmRecord.access!);
        },
        error: (error) => {
          console.warn('Failed to get CRM token:', error);
          this.isLoadingCRM = false;
          if (error.status === 403) {
            this.crmError = 'CRM access is not available';
          } else {
            this.crmError = 'Failed to access CRM record';
          }
        },
      });
  }

  private navigateToCRM(crmLeadId: string, token: string): void {
    const recordId = crmLeadId.includes('x')
      ? crmLeadId.split('x')[1]
      : crmLeadId;
    const crmUrl = `${environment.crmUrl}/index.php?module=Leads&view=Detail&record=${recordId}&app=MARKETING&token=${token}`;
    window.open(crmUrl, '_blank');
    this.isLoadingCRM = false;
  }

  openScoreDataSourcePopup(): void {
    if (this.showScore) {
      // If score is already shown, just toggle it off
      this.showScore = false;
      this.useCustomData = null;
    } else {
      const isCustomSource =
        this.recordDetails?.source_type.toLowerCase() === 'custom';
      const hasOutreachFormLoanType = this.isLoanFormFilled(
        isCustomSource ? false : true,
      );

      const hasManualLoanType =
        isCustomSource &&
        (this.recordDetails?.detail.properties.some(
          (p) => p.property === 'loan_type' && p.value === 'Term Loans',
        ) ||
          (this.recordDetails?.detail.properties.some(
            (p) =>
              p.property === 'capital_product_type' &&
              !(p.value.length == 1 && p.value[0] === 'Grant Programs'),
          ) &&
            this.recordDetails?.detail.properties.some(
              (p) =>
                p.property === 'will_fill_prescreening' && p.value === 'Yes',
            )));

      const hasBothSources = hasOutreachFormLoanType && hasManualLoanType;

      // const hasTotalScore = this.recordDetails?.detail.properties.some(
      //   (p) => p.property === 'total_score' && p.value !== null,
      // );
      const hasTotalScore = false; // Hide WNCSBI score for now

      if (hasBothSources) {
        // Show the data source selection popup
        this.showScoreDataSourcePopup = true;
        this.useCustomData = null;
      } else if (hasOutreachFormLoanType || hasManualLoanType) {
        // Directly show score - backend will use the available source
        this.useCustomData = hasManualLoanType ? true : false;
        this.showScore = true;
      } else if (
        hasTotalScore &&
        !hasOutreachFormLoanType &&
        !hasManualLoanType
      ) {
        this.useCustomData = null;
        this.showScore = true;
      }
    }
  }

  confirmScoreDataSource(): void {
    if (this.useCustomData !== null) {
      this.showScoreDataSourcePopup = false;
      this.showScore = true;
    }
  }

  onScoreDataSourceSelected(): void {
    // Directly show score when user selects a data source
    if (this.useCustomData !== null) {
      this.showScoreDataSourcePopup = false;
      this.showScore = true;
    }
  }

  cancelScoreDataSource(): void {
    this.showScoreDataSourcePopup = false;
    this.useCustomData = null;
  }

  formatCityName(name: string): string {
    if (!name) return '';

    return name
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  isCountryProperty(property: { property_name?: string }): boolean {
    return property.property_name?.toLowerCase() === 'country';
  }

  areActionButtonsAvailable(): boolean {
    return this.isActionButtonsAvailable ? this.canEditDelete : false;
  }

  viewProfile(company: Company): void {
    this.router.navigate([`/sme/${company.created_by_user?.id}/profile`], {
      state: { fromRecordDetails: true },
    });
  }

  showVlfClaimModal = false;
  isClaimingVlf = false;

  openVlfClaimModal(): void {
    if (!this.company?.unique_id) return;
    this.showVlfClaimModal = true;
  }

  confirmClaimVLFLead(): void {
    if (!this.company?.unique_id) return;
    this.isClaimingVlf = true;
    this.leadsService
      .assignLead([this.company])
      .pipe(
        takeUntil(this.destroy$),
        switchMap((res: AssignLeadResponse) => {
          this.isClaimingVlf = false;
          this.showVlfClaimModal = false;
          this.crmStatus = res.crm_status;

          const successData = res.success?.[0];
          if (!successData) {
            const failedReason =
              res.failure?.[0]?.errorMessage || 'Failed to claim VLF lead.';
            this.commonService.triggerToast({
              type: ToastType.Error,
              message: failedReason,
            });
            return throwError(() => new Error(failedReason));
          }

          this.company.leadId = successData.id;
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'VLF Lead claimed successfully!',
          });

          this.showLeadsHistoryInline = false;

          // Update assigned_to
          if (this.recordDetails) {
            this.recordDetails.assigned_to = this.getAssignedToInfo();
          }
          this.company.assigned_to =
            this.recordDetails?.assigned_to || this.getAssignedToInfo();

          // Update quota count immediately
          if (this.recordDetails?.lead_quota) {
            this.recordDetails.lead_quota.lead_count += 1;
          }

          // Update assignees arrays - add current user if not already present
          const currentUserConnection: AssigneeConnectionDetails = {
            connection_name: this.loggedInUserInfo?.connection ?? '',
            id: 0,
            display_name:
              this.loggedInUserInfo?.connection_display_name ??
              this.loggedInUserInfo?.connection ??
              '',
            assigned_on_utc: new Date(),
          };

          if (this.recordDetails) {
            if (!this.recordDetails.assignees) {
              this.recordDetails.assignees = [];
            }
            const existsInRecordDetails = this.recordDetails.assignees.some(
              (assignee) =>
                assignee.connection_name ===
                currentUserConnection.connection_name,
            );
            if (!existsInRecordDetails) {
              this.recordDetails.assignees.push(currentUserConnection);
            }
          }

          if (!this.company.assignees) {
            this.company.assignees = [];
          }
          const existsInCompany = this.company.assignees.some(
            (assignee) =>
              assignee.connection_name ===
              currentUserConnection.connection_name,
          );
          if (!existsInCompany) {
            this.company.assignees = [
              ...this.company.assignees,
              currentUserConnection,
            ];
          }

          this.checkEditDelete();
          this.assignedLead.emit(this.company);
          this.companyDetailsUpdated.emit(this.company);
          this.crmLeadId = null;

          return this.loadLeadAttributes(successData.id);
        }),
      )
      .subscribe({
        next: () => {
          this.refreshInfo(false);
        },
        error: (err) => {
          this.isClaimingVlf = false;
          this.showVlfClaimModal = false;
          console.log(err);
        },
      });
  }

  claimVLFLead(): void {
    this.openVlfClaimModal();
  }
}
